import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Info,
  XCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
  CheckCircle,
  Brain,
  Tag,
  Shuffle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";

import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const CategoryFluency = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();
  const inputRef = useRef(null);

  // Game settings
  const [gameMode, setGameMode] = useState("intro"); // intro, tutorial, play, results
  const [difficulty, setDifficulty] = useState("standard"); // easy, standard, advanced
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [sessionCompleted, setSessionCompleted] = useState(false);

  // Game state
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes default
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentCategory, setCurrentCategory] = useState("");
  const [nextCategory, setNextCategory] = useState("");
  const [submittedItems, setSubmittedItems] = useState([]);
  const [categoryChangeCount, setCategoryChangeCount] = useState(0);
  const [totalItemsGenerated, setTotalItemsGenerated] = useState(0);
  const [uniqueItemsGenerated, setUniqueItemsGenerated] = useState(0);
  const [categorySwitchSpeed, setCategorySwitchSpeed] = useState([]);
  const [validSubmissions, setValidSubmissions] = useState([]);
  const [invalidSubmissions, setInvalidSubmissions] = useState([]);
  const [currentCategoryStartTime, setCurrentCategoryStartTime] =
    useState(null);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);
  const [switchingCategoryNow, setSwitchingCategoryNow] = useState(false);
  const [averageSwitchSpeed, setAverageSwitchSpeed] = useState(0);
  const [clustersFormed, setClustersFormed] = useState(0);
  const [blockedItems, setBlockedItems] = useState([]);

  // Tutorial state
  const [tutorialStep, setTutorialStep] = useState(0);
  const timerRef = useRef(null);
  const gameActive = useRef(false);

  // Colors based on cognitive flexibility category (same as RuleShifter)
  const primaryColor = "from-emerald-500 to-teal-600";
  const primaryColorSolid = "bg-emerald-500";
  const secondaryColor = "text-emerald-300";
  const tertiaryColor = "bg-emerald-500/10";

  // Category sets
  const categorySets = {
    easy: [
      "animals",
      "foods",
      "clothing",
      "transportation",
      "sports",
      "colors",
      "furniture",
      "jobs",
    ],
    standard: [
      "animals",
      "foods",
      "clothing",
      "transportation",
      "sports",
      "countries",
      "tools",
      "musical instruments",
      "body parts",
      "weather",
    ],
    advanced: [
      "animals",
      "foods",
      "countries",
      "musical instruments",
      "occupations",
      "academic subjects",
      "kitchen items",
      "hobbies",
      "medical conditions",
      "languages",
      "emotions",
      "historical figures",
    ],
  };

  // Difficulty settings
  const difficultySettings = {
    easy: {
      timePerCategory: 30, // seconds
      totalGameTime: 120, // 2 minutes
      minimumItemsBeforeSwitch: 1, // items before allowing category change
      switchPenalty: 5, // seconds lost on voluntary switch
      hintAvailable: true,
      validationThreshold: "lenient", // more forgiving validation
    },
    standard: {
      timePerCategory: 20,
      totalGameTime: 120,
      minimumItemsBeforeSwitch: 2,
      switchPenalty: 10,
      hintAvailable: false,
      validationThreshold: "standard",
    },
    advanced: {
      timePerCategory: 15,
      totalGameTime: 120,
      minimumItemsBeforeSwitch: 3,
      switchPenalty: 15,
      hintAvailable: false,
      validationThreshold: "strict", // more demanding validation
    },
  };

  // Common category items for validation (simplified version - in production would be more extensive)
  const commonCategoryItems = {
    animals: [
      // Original list
      "dog",
      "cat",
      "lion",
      "tiger",
      "elephant",
      "giraffe",
      "zebra",
      "horse",
      "cow",
      "sheep",
      "goat",
      "pig",
      "monkey",
      "gorilla",
      "bear",
      "wolf",
      "fox",
      "deer",
      "rabbit",
      "mouse",
      "rat",
      "squirrel",
      "beaver",
      "otter",
      "dolphin",
      "whale",
      "shark",
      "fish",
      "octopus",
      "squid",
      "crab",
      "lobster",
      "butterfly",
      "bee",
      "ant",
      "spider",
      "scorpion",
      "snake",
      "crocodile",
      "alligator",
      "turtle",
      "frog",
      "toad",
      "bird",
      "eagle",
      "hawk",
      "owl",
      "penguin",
      "duck",
      "chicken",

      // Expanded animals
      "puppy",
      "kitten",
      "cub",
      "foal",
      "lamb",
      "calf",
      "piglet",
      "chick",
      "duckling",
      "panda",
      "koala",
      "kangaroo",
      "leopard",
      "cheetah",
      "jaguar",
      "lynx",
      "cougar",
      "panther",
      "raccoon",
      "hedgehog",
      "possum",
      "opossum",
      "skunk",
      "badger",
      "mole",
      "weasel",
      "ferret",
      "hamster",
      "gerbil",
      "guinea pig",
      "chinchilla",
      "chipmunk",
      "bison",
      "buffalo",
      "moose",
      "elk",
      "caribou",
      "antelope",
      "gazelle",
      "impala",
      "giraffe",
      "hippo",
      "hippopotamus",
      "rhinoceros",
      "rhino",
      "warthog",
      "boar",
      "hyena",
      "jackal",
      "coyote",
      "dingo",
      "parrot",
      "parakeet",
      "cockatoo",
      "macaw",
      "canary",
      "finch",
      "sparrow",
      "robin",
      "cardinal",
      "bluejay",
      "woodpecker",
      "hummingbird",
      "seagull",
      "pelican",
      "flamingo",
      "stork",
      "crane",
      "ostrich",
      "emu",
      "crow",
      "raven",
      "vulture",
      "condor",
      "falcon",
      "kestrel",
      "kite",
      "osprey",
      "heron",
      "swan",
      "goose",
      "pigeon",
      "dove",
      "peacock",
      "turkey",
      "pheasant",
      "quail",
      "iguana",
      "chameleon",
      "gecko",
      "lizard",
      "komodo dragon",
      "salamander",
      "newt",
      "toad",
      "tuna",
      "salmon",
      "trout",
      "bass",
      "catfish",
      "goldfish",
      "piranha",
      "swordfish",
      "marlin",
      "manta ray",
      "stingray",
      "eel",
      "seahorse",
      "starfish",
      "jellyfish",
      "coral",
      "clam",
      "oyster",
      "mussel",
      "scallop",
      "snail",
      "slug",
      "worm",
      "centipede",
      "millipede",
      "cockroach",
      "grasshopper",
      "cricket",
      "locust",
      "praying mantis",
      "ladybug",
      "beetle",
      "firefly",
      "dragonfly",
      "moth",
      "caterpillar",
      "larva",
      "pupa",
      "fly",
      "mosquito",
      "tick",
      "mite",
      "flea",
      "louse",
      "wasp",
      "hornet",
      "bumblebee",
      "termite",
      "tarantula",
      "python",
      "cobra",
      "viper",
      "rattlesnake",
      "boa",
      "anaconda",
      "tortoise",
      "terrapin",
      "sea turtle",
      "leatherback",
      "caiman",
      "gharial",
    ],

    foods: [
      // Original list
      "apple",
      "banana",
      "orange",
      "grape",
      "strawberry",
      "blueberry",
      "watermelon",
      "pizza",
      "burger",
      "sandwich",
      "pasta",
      "rice",
      "bread",
      "cheese",
      "yogurt",
      "ice cream",
      "cake",
      "cookie",
      "chocolate",
      "candy",
      "steak",
      "chicken",
      "fish",
      "shrimp",
      "lobster",
      "crab",
      "soup",
      "salad",
      "vegetable",
      "fruit",
      "potato",
      "carrot",
      "onion",
      "garlic",
      "tomato",
      "lettuce",
      "spinach",
      "broccoli",
      "corn",

      // Expanded foods
      // Fruits
      "pear",
      "peach",
      "plum",
      "apricot",
      "nectarine",
      "cherry",
      "lemon",
      "lime",
      "grapefruit",
      "pineapple",
      "mango",
      "papaya",
      "kiwi",
      "fig",
      "date",
      "coconut",
      "avocado",
      "blackberry",
      "raspberry",
      "cranberry",
      "currant",
      "gooseberry",
      "pomegranate",
      "guava",
      "lychee",
      "passion fruit",
      "dragonfruit",
      "persimmon",
      "cantaloupe",
      "honeydew",
      "melon",
      "tangerine",
      "clementine",
      "mandarin",

      // Vegetables
      "cucumber",
      "zucchini",
      "squash",
      "pumpkin",
      "eggplant",
      "bell pepper",
      "chili pepper",
      "jalapeno",
      "cabbage",
      "kale",
      "brussels sprout",
      "cauliflower",
      "celery",
      "asparagus",
      "artichoke",
      "leek",
      "radish",
      "turnip",
      "beet",
      "parsnip",
      "rutabaga",
      "sweet potato",
      "yam",
      "okra",
      "green bean",
      "pea",
      "snow pea",
      "sugar snap pea",
      "chickpea",
      "lentil",
      "black bean",
      "kidney bean",
      "pinto bean",
      "navy bean",
      "mushroom",
      "truffle",
      "seaweed",
      "kale",
      "chard",
      "collard greens",
      "mustard greens",

      // Meats & Proteins
      "beef",
      "pork",
      "lamb",
      "veal",
      "venison",
      "bison",
      "duck",
      "turkey",
      "goose",
      "quail",
      "pheasant",
      "rabbit",
      "bacon",
      "ham",
      "sausage",
      "salami",
      "pepperoni",
      "prosciutto",
      "hot dog",
      "egg",
      "tofu",
      "tempeh",
      "seitan",
      "salmon",
      "tuna",
      "cod",
      "halibut",
      "trout",
      "tilapia",
      "mackerel",
      "sardine",
      "anchovy",
      "mussels",
      "clams",
      "oysters",
      "scallops",

      // Dairy & Alternatives
      "milk",
      "cream",
      "butter",
      "margarine",
      "sour cream",
      "cottage cheese",
      "ricotta",
      "mozzarella",
      "cheddar",
      "brie",
      "camembert",
      "gouda",
      "swiss cheese",
      "parmesan",
      "feta",
      "blue cheese",
      "goat cheese",
      "almond milk",
      "soy milk",
      "oat milk",
      "coconut milk",
      "rice milk",
      "cashew milk",

      // Grains & Starches
      "wheat",
      "oats",
      "barley",
      "rye",
      "quinoa",
      "couscous",
      "bulgur",
      "farro",
      "millet",
      "sorghum",
      "cornmeal",
      "flour",
      "bread crumbs",
      "crouton",
      "tortilla",
      "naan",
      "pita",
      "bagel",
      "croissant",
      "baguette",
      "roll",
      "biscuit",
      "muffin",
      "pancake",
      "waffle",
      "cereal",
      "granola",
      "oatmeal",
      "noodle",
      "spaghetti",
      "macaroni",
      "fettuccine",
      "linguine",
      "lasagna",
      "ravioli",
      "dumpling",

      // Desserts & Sweets
      "pie",
      "tart",
      "cobbler",
      "pudding",
      "custard",
      "brownie",
      "donut",
      "pastry",
      "danish",
      "eclair",
      "cupcake",
      "cheesecake",
      "tiramisu",
      "gelato",
      "sorbet",
      "popsicle",
      "caramel",
      "fudge",
      "taffy",
      "marshmallow",
      "gummy",
      "jelly bean",
      "lollipop",
      "licorice",
      "toffee",
      "brittle",
      "praline",

      // Condiments & Sauces
      "ketchup",
      "mustard",
      "mayonnaise",
      "vinegar",
      "oil",
      "olive oil",
      "soy sauce",
      "hot sauce",
      "salsa",
      "guacamole",
      "hummus",
      "pesto",
      "marinara",
      "alfredo",
      "gravy",
      "dressing",
      "ranch",
      "honey",
      "syrup",
      "jam",
      "jelly",
      "preserves",
      "peanut butter",
      "nutella",
      "butter",
      "margarine",

      // Beverages
      "water",
      "tea",
      "coffee",
      "juice",
      "soda",
      "pop",
      "cola",
      "lemonade",
      "smoothie",
      "milkshake",
      "wine",
      "beer",
      "cocktail",
      "whiskey",
      "vodka",
      "rum",
      "tequila",
      "gin",
      "liqueur",
    ],

    clothing: [
      // Original list
      "shirt",
      "t-shirt",
      "blouse",
      "pants",
      "jeans",
      "shorts",
      "skirt",
      "dress",
      "suit",
      "jacket",
      "coat",
      "sweater",
      "hoodie",
      "sweatshirt",
      "socks",
      "shoes",
      "boots",
      "sandals",
      "sneakers",
      "hat",
      "cap",
      "beanie",
      "scarf",
      "gloves",
      "mittens",
      "underwear",
      "boxers",
      "briefs",
      "bra",
      "swimsuit",
      "bikini",
      "trunks",
      "tie",
      "bowtie",
      "belt",
      "suspenders",

      // Expanded clothing
      "polo",
      "tank top",
      "camisole",
      "tunic",
      "cardigan",
      "vest",
      "blazer",
      "trench coat",
      "parka",
      "windbreaker",
      "raincoat",
      "poncho",
      "cape",
      "chinos",
      "khakis",
      "slacks",
      "corduroys",
      "leggings",
      "tights",
      "jeggings",
      "yoga pants",
      "sweatpants",
      "tracksuit",
      "pajamas",
      "nightgown",
      "robe",
      "bathrobe",
      "overalls",
      "jumpsuit",
      "romper",
      "bodysuit",
      "gown",
      "sundress",
      "cocktail dress",
      "evening dress",
      "wedding dress",
      "tuxedo",
      "three-piece suit",
      "pantsuit",
      "uniform",
      "apron",
      "miniskirt",
      "midi skirt",
      "maxi skirt",
      "pleated skirt",
      "pencil skirt",
      "denim skirt",
      "kilt",
      "sarong",
      "stockings",
      "knee socks",
      "ankle socks",
      "crew socks",
      "tube socks",
      "compression socks",
      "pantyhose",
      "garter",
      "heels",
      "high heels",
      "stilettos",
      "pumps",
      "flats",
      "loafers",
      "oxfords",
      "moccasins",
      "slippers",
      "flip flops",
      "clogs",
      "crocs",
      "espadrilles",
      "wedges",
      "platforms",
      "snow boots",
      "rain boots",
      "cowboy boots",
      "combat boots",
      "ankle boots",
      "hiking boots",
      "work boots",
      "fedora",
      "beret",
      "sun hat",
      "straw hat",
      "cowboy hat",
      "visor",
      "headband",
      "earmuffs",
      "bandana",
      "hijab",
      "turban",
      "infinity scarf",
      "shawl",
      "stole",
      "neckerchief",
      "handkerchief",
      "pocket square",
      "wristband",
      "sweatband",
      "watch",
      "bracelet",
      "anklet",
      "necklace",
      "pendant",
      "locket",
      "choker",
      "earrings",
      "ring",
      "cufflinks",
      "brooch",
      "pin",
      "badges",
      "thong",
      "panties",
      "briefs",
      "boxer briefs",
      "long johns",
      "thermal underwear",
      "undershirt",
      "tank",
      "sports bra",
      "bralette",
      "corset",
      "lingerie",
      "one-piece swimsuit",
      "tankini",
      "swim trunks",
      "board shorts",
      "rash guard",
      "wet suit",
      "ski suit",
      "lab coat",
      "scrubs",
      "cleats",
      "tap shoes",
      "ballet shoes",
      "pointe shoes",
      "dance shoes",
    ],

    transportation: [
      // Original list
      "car",
      "truck",
      "bus",
      "train",
      "subway",
      "tram",
      "bicycle",
      "motorcycle",
      "scooter",
      "skateboard",
      "boat",
      "ship",
      "ferry",
      "yacht",
      "canoe",
      "kayak",
      "airplane",
      "helicopter",
      "jet",
      "rocket",
      "spaceship",
      "hot air balloon",

      // Expanded transportation
      "sedan",
      "coupe",
      "convertible",
      "hatchback",
      "station wagon",
      "SUV",
      "minivan",
      "van",
      "pickup truck",
      "semi-truck",
      "tractor-trailer",
      "moving truck",
      "dump truck",
      "garbage truck",
      "fire truck",
      "ambulance",
      "police car",
      "limousine",
      "taxi",
      "cab",
      "rideshare",
      "uber",
      "lyft",
      "school bus",
      "double-decker bus",
      "trolley",
      "streetcar",
      "cable car",
      "monorail",
      "light rail",
      "passenger train",
      "freight train",
      "bullet train",
      "high-speed rail",
      "steam locomotive",
      "diesel locomotive",
      "electric locomotive",
      "caboose",
      "rail car",
      "metro",
      "underground",
      "tube",
      "tricycle",
      "unicycle",
      "tandem bicycle",
      "mountain bike",
      "road bike",
      "BMX bike",
      "electric bike",
      "moped",
      "dirt bike",
      "cruise ship",
      "ocean liner",
      "sailboat",
      "speedboat",
      "motorboat",
      "rowboat",
      "raft",
      "dinghy",
      "pontoon",
      "jet ski",
      "paddleboard",
      "surfboard",
      "submarine",
      "aircraft carrier",
      "battleship",
      "propeller plane",
      "seaplane",
      "glider",
      "hang glider",
      "paraglider",
      "parachute",
      "blimp",
      "zeppelin",
      "drone",
      "space shuttle",
      "space station",
      "rover",
      "lander",
      "probe",
      "satellite",
      "segway",
      "hoverboard",
      "roller skates",
      "inline skates",
      "sled",
      "toboggan",
      "snowmobile",
      "dogsled",
      "horse",
      "donkey",
      "camel",
      "elephant",
      "rickshaw",
      "carriage",
      "buggy",
      "chariot",
      "gondola",
      "cable car",
      "ski lift",
      "escalator",
      "elevator",
      "moving walkway",
      "zipline",
    ],

    sports: [
      // Original list
      "soccer",
      "football",
      "basketball",
      "baseball",
      "tennis",
      "golf",
      "hockey",
      "volleyball",
      "rugby",
      "cricket",
      "badminton",
      "swimming",
      "diving",
      "running",
      "track",
      "field",
      "gymnastics",
      "boxing",
      "wrestling",
      "martial arts",
      "skiing",
      "snowboarding",
      "skating",
      "surfing",
      "cycling",

      // Expanded sports
      "american football",
      "aussie rules football",
      "ice hockey",
      "field hockey",
      "table tennis",
      "ping pong",
      "racquetball",
      "squash",
      "pickleball",
      "lacrosse",
      "water polo",
      "handball",
      "softball",
      "t-ball",
      "kickball",
      "dodgeball",
      "bowling",
      "archery",
      "shooting",
      "fencing",
      "javelin",
      "discus",
      "shot put",
      "hammer throw",
      "high jump",
      "long jump",
      "triple jump",
      "pole vault",
      "hurdles",
      "relay",
      "marathon",
      "triathlon",
      "pentathlon",
      "decathlon",
      "cross country",
      "sprinting",
      "jogging",
      "race walking",
      "figure skating",
      "speed skating",
      "ice dancing",
      "roller skating",
      "roller derby",
      "skateboarding",
      "snowshoeing",
      "curling",
      "bobsled",
      "luge",
      "skeleton",
      "biathlon",
      "slalom",
      "downhill skiing",
      "cross-country skiing",
      "ski jumping",
      "snowmobile racing",
      "bodyboarding",
      "windsurfing",
      "kitesurfing",
      "wakeboarding",
      "waterskiing",
      "paddleboarding",
      "canoeing",
      "kayaking",
      "rafting",
      "rowing",
      "sailing",
      "mountain climbing",
      "rock climbing",
      "bouldering",
      "hiking",
      "backpacking",
      "orienteering",
      "parkour",
      "bmx",
      "mountain biking",
      "road cycling",
      "track cycling",
      "motorcycle racing",
      "auto racing",
      "formula 1",
      "nascar",
      "drag racing",
      "horse racing",
      "polo",
      "dressage",
      "rodeo",
      "bullfighting",
      "judo",
      "karate",
      "taekwondo",
      "aikido",
      "jiu-jitsu",
      "muay thai",
      "kickboxing",
      "mma",
      "sumo",
      "capoeira",
      "kendo",
      "fencing",
      "weightlifting",
      "bodybuilding",
      "crossfit",
      "powerlifting",
      "strongman",
      "gymnastics",
      "trampolining",
      "cheerleading",
      "acrobatics",
      "ballet",
      "dance",
      "yoga",
      "pilates",
      "tai chi",
      "qigong",
      "ultimate frisbee",
      "disc golf",
      "billiards",
      "pool",
      "snooker",
      "darts",
      "chess",
      "checkers",
      "go",
      "backgammon",
      "poker",
      "bridge",
      "esports",
      "gaming",
      "fishing",
      "hunting",
      "skeet shooting",
      "paintball",
      "airsoft",
      "laser tag",
      "bungee jumping",
      "skydiving",
      "paragliding",
      "hang gliding",
      "hot air ballooning",
    ],

    countries: [
      // Original list
      "usa",
      "canada",
      "mexico",
      "brazil",
      "argentina",
      "chile",
      "peru",
      "colombia",
      "united kingdom",
      "england",
      "france",
      "germany",
      "italy",
      "spain",
      "portugal",
      "greece",
      "russia",
      "china",
      "japan",
      "korea",
      "india",
      "australia",
      "egypt",
      "south africa",
      "kenya",
      "nigeria",

      // Expanded countries
      "united states",
      "united states of america",
      "america",
      "great britain",
      "uk",
      "scotland",
      "wales",
      "northern ireland",
      "ireland",
      "north korea",
      "south korea",
      "new zealand",
      "indonesia",
      "malaysia",
      "philippines",
      "singapore",
      "thailand",
      "vietnam",
      "cambodia",
      "laos",
      "myanmar",
      "bangladesh",
      "pakistan",
      "sri lanka",
      "nepal",
      "bhutan",
      "afghanistan",
      "iran",
      "iraq",
      "saudi arabia",
      "qatar",
      "uae",
      "united arab emirates",
      "kuwait",
      "bahrain",
      "oman",
      "yemen",
      "syria",
      "lebanon",
      "israel",
      "palestine",
      "jordan",
      "turkey",
      "cyprus",
      "kazakhstan",
      "uzbekistan",
      "turkmenistan",
      "kyrgyzstan",
      "tajikistan",
      "azerbaijan",
      "armenia",
      "georgia",
      "ukraine",
      "belarus",
      "moldova",
      "romania",
      "bulgaria",
      "hungary",
      "poland",
      "czech republic",
      "czechia",
      "slovakia",
      "austria",
      "switzerland",
      "liechtenstein",
      "monaco",
      "san marino",
      "vatican",
      "vatican city",
      "andorra",
      "slovenia",
      "croatia",
      "bosnia",
      "herzegovina",
      "bosnia and herzegovina",
      "serbia",
      "montenegro",
      "north macedonia",
      "macedonia",
      "albania",
      "kosovo",
      "sweden",
      "norway",
      "denmark",
      "finland",
      "iceland",
      "estonia",
      "latvia",
      "lithuania",
      "belgium",
      "netherlands",
      "holland",
      "luxembourg",
      "algeria",
      "libya",
      "tunisia",
      "morocco",
      "mauritania",
      "mali",
      "niger",
      "chad",
      "sudan",
      "south sudan",
      "eritrea",
      "ethiopia",
      "djibouti",
      "somalia",
      "uganda",
      "rwanda",
      "burundi",
      "tanzania",
      "mozambique",
      "malawi",
      "zambia",
      "zimbabwe",
      "botswana",
      "namibia",
      "lesotho",
      "eswatini",
      "swaziland",
      "madagascar",
      "comoros",
      "mauritius",
      "seychelles",
      "cape verde",
      "sao tome and principe",
      "guinea-bissau",
      "guinea",
      "sierra leone",
      "liberia",
      "ivory coast",
      "cote d'ivoire",
      "burkina faso",
      "ghana",
      "togo",
      "benin",
      "cameroon",
      "central african republic",
      "equatorial guinea",
      "gabon",
      "republic of the congo",
      "congo",
      "democratic republic of the congo",
      "dr congo",
      "drc",
      "ecuador",
      "venezuela",
      "guyana",
      "suriname",
      "french guiana",
      "bolivia",
      "paraguay",
      "uruguay",
      "cuba",
      "jamaica",
      "haiti",
      "dominican republic",
      "dominica",
      "puerto rico",
      "bahamas",
      "barbados",
      "trinidad",
      "tobago",
      "trinidad and tobago",
      "grenada",
      "st lucia",
      "saint lucia",
      "st vincent",
      "saint vincent",
      "antigua",
      "barbuda",
      "antigua and barbuda",
      "st kitts",
      "saint kitts",
      "nevis",
      "saint kitts and nevis",
      "panama",
      "costa rica",
      "nicaragua",
      "honduras",
      "el salvador",
      "guatemala",
      "belize",
      "greenland",
      "iceland",
      "papua new guinea",
      "solomon islands",
      "fiji",
      "vanuatu",
      "samoa",
      "tonga",
      "kiribati",
      "tuvalu",
      "nauru",
      "micronesia",
      "marshall islands",
      "palau",
      "taiwan",
    ],

    colors: [
      // Original list
      "red",
      "blue",
      "green",
      "yellow",
      "orange",
      "purple",
      "pink",
      "brown",
      "black",
      "white",
      "gray",
      "gold",
      "silver",
      "bronze",
      "turquoise",
      "teal",
      "navy",
      "maroon",

      // Expanded colors
      "scarlet",
      "crimson",
      "ruby",
      "vermilion",
      "cherry",
      "burgundy",
      "wine",
      "blood red",
      "magenta",
      "fuchsia",
      "hot pink",
      "rose",
      "salmon",
      "coral",
      "peach",
      "blush",
      "indigo",
      "azure",
      "cobalt",
      "royal blue",
      "sky blue",
      "baby blue",
      "aqua",
      "cyan",
      "seafoam",
      "mint",
      "lime",
      "olive",
      "emerald",
      "forest green",
      "jade",
      "sage",
      "amber",
      "marigold",
      "mustard",
      "lemon",
      "cream",
      "ivory",
      "beige",
      "tan",
      "khaki",
      "chocolate",
      "coffee",
      "caramel",
      "chestnut",
      "mahogany",
      "sepia",
      "umber",
      "lavender",
      "lilac",
      "violet",
      "periwinkle",
      "plum",
      "mauve",
      "amethyst",
      "orchid",
      "slate",
      "charcoal",
      "ash",
      "onyx",
      "ebony",
      "platinum",
      "pearl",
      "taupe",
      "copper",
      "rust",
      "sienna",
      "terracotta",
      "ochre",
      "cerulean",
      "sapphire",
      "chartreuse",
    ],

    furniture: [
      // Original list
      "table",
      "chair",
      "sofa",
      "couch",
      "desk",
      "bed",
      "dresser",
      "drawer",
      "cabinet",
      "shelf",
      "bookshelf",
      "stool",
      "ottoman",
      "bench",
      "lamp",
      "mirror",
      "nightstand",

      // Expanded furniture
      "coffee table",
      "end table",
      "side table",
      "dining table",
      "console table",
      "picnic table",
      "folding table",
      "card table",
      "armchair",
      "recliner",
      "wingback chair",
      "rocking chair",
      "office chair",
      "swivel chair",
      "bean bag chair",
      "dining chair",
      "bar stool",
      "counter stool",
      "footstool",
      "step stool",
      "loveseat",
      "sectional",
      "futon",
      "sleeper sofa",
      "daybed",
      "chaise lounge",
      "papasan",
      "hammock",
      "writing desk",
      "computer desk",
      "standing desk",
      "secretary desk",
      "roll top desk",
      "credenza",
      "king bed",
      "queen bed",
      "full bed",
      "twin bed",
      "bunk bed",
      "loft bed",
      "trundle bed",
      "four-poster bed",
      "canopy bed",
      "murphy bed",
      "platform bed",
      "water bed",
      "crib",
      "cradle",
      "bassinet",
      "toddler bed",
      "chest of drawers",
      "trunk",
      "armoire",
      "wardrobe",
      "entertainment center",
      "hutch",
      "china cabinet",
      "display cabinet",
      "file cabinet",
      "medicine cabinet",
      "pantry",
      "buffet",
      "sideboard",
      "kitchen island",
      "bookcase",
      "etagere",
      "shelving unit",
      "curio cabinet",
      "bar cabinet",
      "wine rack",
      "spice rack",
      "coat rack",
      "hat rack",
      "shoe rack",
      "magazine rack",
      "vanity",
      "dressing table",
      "end table",
      "side table",
      "floor lamp",
      "table lamp",
      "desk lamp",
      "sconce",
      "chandelier",
      "pendant light",
      "track lighting",
      "wall mirror",
      "floor mirror",
      "vanity mirror",
      "full-length mirror",
      "bedside table",
      "fireplace",
      "room divider",
      "folding screen",
      "grandfather clock",
      "cuckoo clock",
      "mantel clock",
    ],

    jobs: [
      // Original list from occupations since it's more comprehensive
      "doctor",
      "nurse",
      "teacher",
      "professor",
      "engineer",
      "programmer",
      "developer",
      "lawyer",
      "judge",
      "accountant",
      "banker",
      "chef",
      "cook",
      "waiter",
      "waitress",
      "cashier",
      "clerk",
      "manager",
      "director",
      "ceo",
      "president",
      "police",
      "firefighter",
      "paramedic",
      "soldier",
      "pilot",
      "flight attendant",
      "actor",
      "actress",
      "musician",
      "artist",
      "writer",
      "author",
      "journalist",
      "reporter",
      "photographer",
      "designer",

      // Expanded jobs
      "surgeon",
      "pediatrician",
      "psychiatrist",
      "dentist",
      "orthodontist",
      "pharmacist",
      "physician assistant",
      "nurse practitioner",
      "midwife",
      "physical therapist",
      "occupational therapist",
      "speech therapist",
      "psychologist",
      "therapist",
      "counselor",
      "social worker",
      "principal",
      "dean",
      "tutor",
      "instructor",
      "teaching assistant",
      "coach",
      "trainer",
      "civil engineer",
      "mechanical engineer",
      "electrical engineer",
      "chemical engineer",
      "aerospace engineer",
      "biomedical engineer",
      "software engineer",
      "data scientist",
      "web developer",
      "app developer",
      "game developer",
      "system administrator",
      "network engineer",
      "cybersecurity analyst",
      "IT support",
      "database administrator",
      "product manager",
      "project manager",
      "attorney",
      "paralegal",
      "legal assistant",
      "prosecutor",
      "tax attorney",
      "corporate lawyer",
      "public defender",
      "financial advisor",
      "analyst",
      "auditor",
      "bookkeeper",
      "tax preparer",
      "investment banker",
      "loan officer",
      "insurance agent",
      "underwriter",
      "actuary",
      "economist",
      "executive chef",
      "sous chef",
      "pastry chef",
      "line cook",
      "baker",
      "bartender",
      "barista",
      "host",
      "hostess",
      "busser",
      "dishwasher",
      "server",
      "retail associate",
      "sales associate",
      "sales representative",
      "stockroom associate",
      "merchandiser",
      "store manager",
      "assistant manager",
      "supervisor",
      "team leader",
      "coordinator",
      "administrator",
      "receptionist",
      "secretary",
      "administrative assistant",
      "office manager",
      "human resources",
      "recruiter",
      "hiring manager",
      "chief financial officer",
      "cfo",
      "chief operating officer",
      "coo",
      "chief technology officer",
      "cto",
      "chief marketing officer",
      "cmo",
      "vice president",
      "vp",
      "executive",
      "entrepreneur",
      "business owner",
      "small business owner",
      "consultant",
      "contractor",
      "freelancer",
      "police officer",
      "detective",
      "sheriff",
      "trooper",
      "security guard",
      "correctional officer",
      "warden",
      "emt",
      "emergency medical technician",
      "lifeguard",
      "park ranger",
      "coast guard",
      "navy",
      "army",
      "air force",
      "marines",
      "military",
      "captain",
      "lieutenant",
      "sergeant",
      "commander",
      "general",
      "admiral",
      "astronaut",
      "flight engineer",
      "air traffic controller",
      "conductor",
      "driver",
      "truck driver",
      "delivery driver",
      "taxi driver",
      "uber driver",
      "chauffeur",
      "bus driver",
      "train conductor",
      "captain",
      "skipper",
      "boatswain",
      "deckhand",
      "actor",
      "actress",
      "performer",
      "model",
      "dancer",
      "singer",
      "comedian",
      "stuntman",
      "stuntwoman",
      "director",
      "producer",
      "screenwriter",
      "editor",
      "camera operator",
      "cinematographer",
      "sound technician",
      "lighting technician",
      "makeup artist",
      "costume designer",
      "set designer",
      "painter",
      "sculptor",
      "illustrator",
      "graphic designer",
      "fashion designer",
      "interior designer",
      "architect",
      "landscape architect",
      "urban planner",
      "novelist",
      "poet",
      "playwright",
      "biographer",
      "blogger",
      "content creator",
      "copywriter",
      "technical writer",
      "editor",
      "publisher",
      "agent",
      "publicist",
      "anchor",
      "broadcaster",
      "news anchor",
      "commentator",
      "dj",
      "radio host",
      "podcaster",
      "videographer",
      "filmmaker",
      "animator",
      "game designer",
      "historian",
      "archaeologist",
      "anthropologist",
      "sociologist",
      "political scientist",
      "researcher",
      "scientist",
      "biologist",
      "chemist",
      "physicist",
      "astronomer",
      "geologist",
      "meteorologist",
      "zoologist",
      "botanist",
      "ecologist",
      "environmental scientist",
      "marine biologist",
      "geneticist",
      "microbiologist",
      "neuroscientist",
      "paleontologist",
      "veterinarian",
      "vet tech",
      "animal trainer",
      "zookeeper",
      "farmer",
      "rancher",
      "agricultural worker",
      "fisherman",
      "logger",
      "miner",
      "construction worker",
      "carpenter",
      "electrician",
      "plumber",
      "welder",
      "roofer",
      "painter",
      "landscaper",
      "gardener",
      "janitor",
      "custodian",
      "housekeeper",
      "maid",
      "cleaner",
      "exterminator",
      "mechanic",
      "auto mechanic",
      "technician",
      "machinist",
      "factory worker",
      "assembly line worker",
      "forklift operator",
      "crane operator",
      "blacksmith",
      "tailor",
      "seamstress",
      "cobbler",
      "barber",
      "hairstylist",
      "cosmetologist",
      "esthetician",
      "nail technician",
      "massage therapist",
      "personal trainer",
      "fitness instructor",
      "nutritionist",
      "dietitian",
      "healthcare worker",
      "home health aide",
      "caregiver",
      "childcare worker",
      "daycare provider",
      "nanny",
      "babysitter",
      "elder care",
      "realtor",
      "real estate agent",
      "property manager",
      "appraiser",
      "broker",
      "travel agent",
      "tour guide",
      "translator",
      "interpreter",
      "curator",
      "librarian",
      "archivist",
    ],
    tools: [
      "hammer",
      "screwdriver",
      "wrench",
      "pliers",
      "saw",
      "drill",
      "nail",
      "screw",
      "bolt",
      "nut",
      "washer",
      "tape measure",
      "level",
      "tape measure",
      "ruler",
      "scissors",
      "knife",
      "shovel",
      "rake",
      "hoe",
      "axe",
      "chisel",
      "mallet",
      "sledgehammer",
      "claw hammer",
      "ball-peen hammer",
      "framing hammer",
      "tack hammer",
      "flathead screwdriver",
      "phillips screwdriver",
      "torx screwdriver",
      "hex key",
      "allen wrench",
      "socket wrench",
      "crescent wrench",
      "pipe wrench",
      "monkey wrench",
      "adjustable wrench",
      "torque wrench",
      "needle-nose pliers",
      "slip-joint pliers",
      "locking pliers",
      "vise-grips",
      "wire cutters",
      "crimping pliers",
      "channel locks",
      "hand saw",
      "hacksaw",
      "coping saw",
      "jigsaw",
      "circular saw",
      "table saw",
      "miter saw",
      "band saw",
      "reciprocating saw",
      "chainsaw",
      "power drill",
      "impact driver",
      "drill press",
      "auger",
      "rotary tool",
      "dremel",
      "spirit level",
      "laser level",
      "bubble level",
      "measuring tape",
      "folding ruler",
      "yardstick",
      "caliper",
      "square",
      "speed square",
      "protractor",
      "straight edge",
      "shears",
      "tin snips",
      "pruning shears",
      "utility knife",
      "box cutter",
      "exacto knife",
      "carpet knife",
      "putty knife",
      "paint scraper",
      "spade",
      "trowel",
      "post hole digger",
      "snow shovel",
      "leaf rake",
      "garden rake",
      "thatcher",
      "pickaxe",
      "pitchfork",
      "garden fork",
      "cultivator",
      "hoe",
      "edger",
      "lawn mower",
      "weed eater",
      "string trimmer",
      "brush cutter",
    ],
    "musical instruments": [
      "guitar",
      "piano",
      "drums",
      "violin",
      "cello",
      "bass",
      "trumpet",
      "trombone",
      "flute",
      "clarinet",
      "saxophone",
      "oboe",
      "harp",
      "accordion",
      "harmonica",
      "keyboard",
      "synthesizer",
      "xylophone",
      "tambourine",
      "maracas",
      "triangle",
      "bongos",
      "conga drums",
    ],
    "body parts": [
      "head",
      "hair",
      "face",
      "eye",
      "penis",
      "vagina",
      "boobs",
      "ear",
      "nose",
      "mouth",
      "lip",
      "tongue",
      "cheek",
      "chin",
      "forehead",
      "jaw",
      "throat",
      "skull",
      "brain",
      "heart",
      "lung",
      "liver",
      "kidney",
      "stomach",
      "intestine",
      "bladder",
      "skin",
      "bone",
      "muscle",
      "blood",
      "vein",
      "artery",
      "nail",
      "toe nail",
      "fingernail",
      "elbow",
      "knee",
      "ankle",
      "wrist",
      "shoulder",
      "hip",
      "back",
      "waist",
      "belly",
      "butt",
      "thigh",
      "calf",
      "foot",
      "toe",
      "thumb",
      "index finger",
      "middle finger",
      "ring finger",
      "pinky finger",
      "palm",
      "sole",
      "heel",
      "forearm",
      "teeth",
      "neck",
      "shoulder",
      "arm",
      "elbow",
      "wrist",
      "hand",
      "finger",
      "thumb",
      "chest",
      "stomach",
      "back",
      "hip",
      "leg",
      "knee",
      "ankle",
      "foot",
      "toe",
    ],
    weather: [
      "rain",
      "snow",
      "sleet",
      "hail",
      "fog",
      "mist",
      "storm",
      "thunderstorm",
      "blizzard",
      "hurricane",
      "tornado",
      "cyclone",
      "typhoon",
      "earthquake",
      "tsunami",
      "landslide",
      "avalanche",
      "drought",
      "flood",
      "heatwave",
      "cold snap",
      "wind chill",
      "humidity",
      "dew point",
      "barometer",
      "thermometer",
      "anemometer",
      "rain gauge",
      "weather vane",
      "radar",
      "satellite",
      "weather balloon",
      "weather map",
      "forecast",
      "meteorologist",
      "sunny",
      "cloudy",
      "partly cloudy",
      "overcast",
      "clear",
      "sunny",
      "partly sunny",
      "mostly sunny",
      "mostly cloudy",
      "thunder",
      "lightning",
      "rainbow",
      "sunrise",
      "sunset",
      "day",
      "night",
      "morning",
      "afternoon",
      "cloud",
      "sun",
      "sunshine",
      "wind",
      "breeze",
      "hurricane",
      "tornado",
      "thunderstorm",
      "lightning",
      "thunder",
      "frost",
      "ice",
      "humidity",
      "heat",
      "cold",
    ],
    occupations: [
      "doctor",
      "nurse",
      "teacher",
      "professor",
      "engineer",
      "programmer",
      "developer",
      "lawyer",
      "attorney",
      "judge",
      "accountant",
      "banker",
      "chef",
      "cook",
      "waiter",
      "waitress",
      "cashier",
      "clerk",
      "manager",
      "director",
      "ceo",
      "president",
      "police",
      "paramedic",
      "emergency medical technician",
      "firefighter",
      "paramedic",
      "soldier",
      "pilot",
      "flight attendant",
      "actor",
      "actress",
      "musician",
      "artist",
      "writer",
      "author",
      "journalist",
      "reporter",
      "photographer",
      "designer",
    ],
    "academic subjects": [
      "math",
      "mathematics",
      "algebra",
      "geometry",
      "calculus",
      "statistics",
      "physics",
      "chemistry",
      "biology",
      "anatomy",
      "physiology",
      "earth science",
      "geology",
      "astronomy",
      "environmental science",
      "computer science",
      "psychology",
      "sociology",
      "anthropology",
      "history",
      "geography",
      "economics",
      "political science",
      "literature",
      "english",
      "writing",
      "reading",
      "grammar",
      "language",
      "linguistics",
      "philosophy",
      "ethics",
      "art",
      "music",
    ],
    "kitchen items": [
      // Original list
      "pot",
      "pan",
      "kettle",
      "spoon",
      "fork",
      "knife",
      "spatula",
      "peeler",
      "grater",
      "whisk",
      "tongs",
      "ladle",
      "measuring cup",
      "measuring spoon",
      "bowl",
      "plate",
      "cup",
      "mug",
      "glass",
      "colander",
      "strainer",
      "blender",
      "mixer",
      "food processor",
      "microwave",
      "oven",
      "stove",
      "refrigerator",

      // Expanded kitchen items
      // Cookware
      "frying pan",
      "sauté pan",
      "saucepan",
      "stock pot",
      "dutch oven",
      "pressure cooker",
      "slow cooker",
      "crock pot",
      "instant pot",
      "wok",
      "griddle",
      "casserole dish",
      "baking dish",
      "roasting pan",
      "sheet pan",
      "cookie sheet",
      "muffin tin",
      "cake pan",
      "pie plate",
      "loaf pan",
      "bundt pan",
      "springform pan",
      "cast iron skillet",
      "non-stick pan",
      "grill pan",
      "double boiler",
      "steamer basket",
      "rice cooker",
      "air fryer",
      "deep fryer",
      "waffle maker",
      "panini press",
      "electric kettle",
      "tea kettle",
      "coffee maker",
      "espresso machine",
      "french press",
      "pour-over",

      // Utensils & Tools
      "table spoon",
      "tea spoon",
      "serving spoon",
      "slotted spoon",
      "wooden spoon",
      "soup spoon",
      "dessert spoon",
      "dinner fork",
      "salad fork",
      "serving fork",
      "carving fork",
      "cocktail fork",
      "butter knife",
      "steak knife",
      "bread knife",
      "carving knife",
      "chef's knife",
      "paring knife",
      "cleaver",
      "santoku knife",
      "boning knife",
      "pizza cutter",
      "vegetable peeler",
      "potato peeler",
      "box grater",
      "microplane",
      "zester",
      "citrus reamer",
      "garlic press",
      "potato masher",
      "meat tenderizer",
      "rolling pin",
      "pastry brush",
      "basting brush",
      "meat thermometer",
      "kitchen timer",
      "candy thermometer",
      "scale",
      "ice cream scoop",
      "melon baller",
      "apple corer",
      "can opener",
      "bottle opener",
      "corkscrew",
      "wine opener",
      "cheese grater",
      "nutcracker",
      "kitchen scissors",
      "kitchen shears",
      "poultry shears",
      "salad spinner",
      "mortar and pestle",
      "funnel",
      "sifter",
      "flour sifter",
      "baster",
      "turkey baster",
      "egg slicer",
      "egg separator",
      "pizza stone",
      "pizza peel",
      "mandoline",
      "spiralizer",
      "juicer",
      "citrus juicer",

      // Dishware & Servingware
      "dinner plate",
      "salad plate",
      "appetizer plate",
      "dessert plate",
      "soup bowl",
      "cereal bowl",
      "mixing bowl",
      "serving bowl",
      "butter dish",
      "gravy boat",
      "sauce boat",
      "sugar bowl",
      "creamer",
      "milk pitcher",
      "teacup",
      "coffee cup",
      "espresso cup",
      "demitasse cup",
      "tumbler",
      "water glass",
      "wine glass",
      "champagne flute",
      "beer glass",
      "shot glass",
      "coffee mug",
      "travel mug",
      "thermos",
      "serving platter",
      "charger plate",
      "cake stand",
      "trivet",
      "coaster",
      "napkin holder",
      "salt shaker",
      "pepper shaker",
      "salt and pepper mills",
      "oil dispenser",
      "vinegar dispenser",
      "soap dispenser",
      "cutting board",
      "cheese board",
      "carving board",
      "serving tray",
      "ice bucket",
      "wine bucket",
      "champagne bucket",
      "salad tongs",
      "ice tongs",

      // Storage & Organization
      "food storage container",
      "tupperware",
      "plastic wrap",
      "aluminum foil",
      "parchment paper",
      "wax paper",
      "freezer bag",
      "sandwich bag",
      "ziplock bag",
      "spice rack",
      "spice jar",
      "utensil holder",
      "knife block",
      "dish rack",
      "drying rack",
      "pot rack",
      "cookbook stand",
      "paper towel holder",
      "trash can",
      "compost bin",
      "recycling bin",
      "garbage disposal",

      // Linens & Textiles
      "dish towel",
      "kitchen towel",
      "pot holder",
      "oven mitt",
      "apron",
      "napkin",
      "table cloth",
      "place mat",
      "dish cloth",
      "kitchen rug",
      "splatter guard",
      "drain board",

      // Appliances (larger)
      "dishwasher",
      "freezer",
      "wine refrigerator",
      "toaster",
      "toaster oven",
      "convection oven",
      "range hood",
      "induction cooktop",
      "gas range",
      "electric range",
      "garbage disposal",
      "range hood",
      "exhaust fan",
      "ice maker",
      "water filter",
      "under-cabinet lighting",
    ],
    hobbies: [
      // Original list
      "reading",
      "writing",
      "painting",
      "drawing",
      "photography",
      "gardening",
      "cooking",
      "baking",
      "knitting",
      "crochet",
      "sewing",
      "woodworking",
      "fishing",
      "hunting",
      "camping",
      "hiking",
      "swimming",
      "running",
      "biking",
      "yoga",
      "meditation",
      "playing music",
      "singing",
      "dancing",
      "collecting",
      "gaming",
      "puzzles",
      "chess",
      "cards",

      // Expanded hobbies
      // Reading & Writing
      "book club",
      "literary criticism",
      "poetry",
      "novels",
      "fiction",
      "non-fiction",
      "biographies",
      "memoirs",
      "journaling",
      "blogging",
      "creative writing",
      "screenwriting",
      "playwriting",
      "copywriting",
      "technical writing",
      "proofreading",
      "editing",
      "publishing",
      "bookbinding",
      "calligraphy",
      "letter writing",
      "storytelling",

      // Visual Arts
      "oil painting",
      "watercolor painting",
      "acrylic painting",
      "sketching",
      "illustration",
      "graphic design",
      "digital art",
      "animation",
      "cartooning",
      "caricature",
      "portraiture",
      "landscape painting",
      "still life",
      "abstract art",
      "printmaking",
      "linocut",
      "etching",
      "screen printing",
      "pastel",
      "charcoal drawing",
      "ink drawing",
      "sculpting",
      "pottery",
      "ceramics",
      "clay modeling",
      "paper mache",
      "origami",
      "collage",
      "mixed media",
      "scrapbooking",
      "adult coloring books",

      // Photography & Film
      "portrait photography",
      "landscape photography",
      "wildlife photography",
      "macro photography",
      "street photography",
      "fashion photography",
      "sports photography",
      "night photography",
      "aerial photography",
      "underwater photography",
      "photojournalism",
      "photo editing",
      "filmmaking",
      "videography",
      "documentary",
      "vlogging",
      "stop motion",
      "time-lapse photography",

      // Crafts & DIY
      "embroidery",
      "cross-stitch",
      "needlepoint",
      "quilting",
      "appliqué",
      "macramé",
      "weaving",
      "loom weaving",
      "tapestry",
      "rug making",
      "felting",
      "leatherworking",
      "jewelry making",
      "beading",
      "wire wrapping",
      "candle making",
      "soap making",
      "paper crafting",
      "card making",
      "scrapbooking",
      "decoupage",
      "basket weaving",
      "furniture restoration",
      "upholstery",
      "glass blowing",
      "stained glass",
      "mosaic",
      "pottery",
      "ceramics",
      "model building",
      "miniature painting",
      "dollhouse making",
      "terrarium building",
      "flower arranging",
      "ikebana",
      "bonsai",
      "home decorating",
      "upcycling",
      "repurposing",

      // Outdoors & Nature
      "bird watching",
      "wildlife spotting",
      "stargazing",
      "astronomy",
      "rock collecting",
      "fossil hunting",
      "metal detecting",
      "beachcombing",
      "foraging",
      "mushroom hunting",
      "botany",
      "herbalism",
      "backpacking",
      "mountaineering",
      "rock climbing",
      "bouldering",
      "caving",
      "spelunking",
      "kayaking",
      "canoeing",
      "rafting",
      "paddleboarding",
      "surfing",
      "windsurfing",
      "kitesurfing",
      "sailing",
      "boating",
      "scuba diving",
      "snorkeling",
      "fly fishing",
      "ice fishing",
      "bow hunting",
      "target shooting",
      "archery",
      "horseback riding",
      "trail riding",
      "orienteering",
      "geocaching",
      "outdoor photography",
      "wilderness survival",

      // Fitness & Sports
      "weightlifting",
      "bodybuilding",
      "crossfit",
      "pilates",
      "tai chi",
      "qigong",
      "martial arts",
      "karate",
      "taekwondo",
      "judo",
      "jiu-jitsu",
      "boxing",
      "kickboxing",
      "wrestling",
      "fencing",
      "gymnastics",
      "acrobatics",
      "parkour",
      "trampoline",
      "roller skating",
      "ice skating",
      "skateboarding",
      "snowboarding",
      "skiing",
      "snowshoeing",
      "cycling",
      "mountain biking",
      "BMX",
      "motorcycle riding",
      "ATV riding",
      "golfing",
      "disc golf",
      "bowling",
      "tennis",
      "badminton",
      "table tennis",
      "racquetball",
      "squash",
      "pickleball",
      "volleyball",
      "basketball",
      "football",
      "soccer",
      "baseball",
      "softball",
      "cricket",
      "rugby",
      "hockey",
      "lacrosse",
      "ultimate frisbee",
      "handball",
      "water polo",
      "surfing",
      "wakeboarding",
      "waterskiing",
      "skydiving",
      "paragliding",
      "hang gliding",
      "bungee jumping",
      "rock climbing",

      // Music & Performing Arts
      "piano playing",
      "guitar playing",
      "violin playing",
      "cello playing",
      "flute playing",
      "clarinet playing",
      "saxophone playing",
      "trumpet playing",
      "drums playing",
      "percussion",
      "harmonica playing",
      "ukulele playing",
      "banjo playing",
      "mandolin playing",
      "harp playing",
      "accordion playing",
      "bagpipes playing",
      "beatboxing",
      "DJ-ing",
      "music production",
      "composing",
      "songwriting",
      "music theory",
      "choral singing",
      "opera singing",
      "a cappella",
      "barbershop quartet",
      "musical theater",
      "acting",
      "theater",
      "improv comedy",
      "stand-up comedy",
      "mime",
      "puppetry",
      "ventriloquism",
      "magic",
      "juggling",
      "circus arts",
      "ballet",
      "tap dancing",
      "ballroom dancing",
      "salsa dancing",
      "swing dancing",
      "tango",
      "hip hop dancing",
      "breakdancing",
      "folk dancing",
      "line dancing",
      "belly dancing",
      "pole dancing",
      "hula",
      "flamenco",

      // Collecting & Curating
      "stamp collecting",
      "coin collecting",
      "currency collecting",
      "antique collecting",
      "vintage items",
      "record collecting",
      "comic book collecting",
      "book collecting",
      "art collecting",
      "memorabilia",
      "sports memorabilia",
      "autograph collecting",
      "model cars",
      "model trains",
      "action figures",
      "toy collecting",
      "doll collecting",
      "shell collecting",
      "rock collecting",
      "gemstone collecting",
      "mineral collecting",
      "fossil collecting",
      "insect collecting",
      "butterfly collecting",
      "taxidermy",
      "wine collecting",
      "whiskey collecting",
      "beer brewing",
      "wine making",
      "distilling",

      // Games & Entertainment
      "board games",
      "tabletop games",
      "role-playing games",
      "dungeons and dragons",
      "miniature wargaming",
      "collectible card games",
      "magic the gathering",
      "poker",
      "bridge",
      "mahjong",
      "dominoes",
      "backgammon",
      "checkers",
      "go",
      "sudoku",
      "crossword puzzles",
      "word searches",
      "jigsaw puzzles",
      "rubik's cube",
      "mechanical puzzles",
      "escape rooms",
      "video games",
      "retro gaming",
      "console gaming",
      "PC gaming",
      "mobile gaming",
      "VR gaming",
      "esports",
      "game development",
      "game design",
      "programming",
      "app development",
      "web development",
      "3D modeling",
      "animation",

      // Education & Learning
      "language learning",
      "history study",
      "archaeology",
      "genealogy",
      "philosophy",
      "theology",
      "science",
      "astronomy",
      "physics",
      "chemistry",
      "biology",
      "botany",
      "zoology",
      "mathematics",
      "engineering",
      "architecture",
      "psychology",
      "sociology",
      "anthropology",
      "geography",
      "politics",
      "economics",
      "law study",
      "medicine study",
      "anatomy",
      "neuroscience",
      "linguistics",
      "etymology",
      "lexicography",
      "public speaking",
      "debate",
      "teaching",
      "mentoring",
      "tutoring",
      "volunteering",

      // Social & Community
      "volunteering",
      "charity work",
      "community service",
      "activism",
      "politics",
      "social clubs",
      "book clubs",
      "film clubs",
      "wine tasting",
      "beer tasting",
      "food tasting",
      "cooking clubs",
      "dinner parties",
      "hosting",
      "event planning",
      "reenacting",
      "cosplay",
      "larping",
      "conventions",
      "traveling",
      "cultural exchange",
      "historical reenactment",
      "renaissance faires",
      "museum visiting",
      "gallery visiting",
      "concert going",
      "theater going",
      "opera going",
      "ballet attending",
    ],
    "medical conditions": [
      // Original list
      "cold",
      "flu",
      "fever",
      "headache",
      "migraine",
      "allergy",
      "asthma",
      "diabetes",
      "hypertension",
      "anxiety",
      "depression",
      "insomnia",
      "arthritis",
      "cancer",
      "heart disease",
      "stroke",
      "obesity",
      "malnutrition",
      "fracture",
      "sprain",

      // Expanded medical conditions
      // Respiratory Conditions
      "pneumonia",
      "bronchitis",
      "emphysema",
      "COPD",
      "chronic obstructive pulmonary disease",
      "tuberculosis",
      "pleurisy",
      "pulmonary embolism",
      "pulmonary fibrosis",
      "cystic fibrosis",
      "sleep apnea",
      "rhinitis",
      "sinusitis",
      "laryngitis",
      "pharyngitis",
      "sore throat",
      "tonsillitis",
      "whooping cough",
      "pertussis",
      "croup",
      "respiratory syncytial virus",
      "RSV",

      // Cardiovascular Conditions
      "coronary artery disease",
      "angina",
      "heart attack",
      "myocardial infarction",
      "arrhythmia",
      "atrial fibrillation",
      "heart palpitations",
      "heart murmur",
      "heart valve disease",
      "congestive heart failure",
      "cardiomyopathy",
      "pericarditis",
      "endocarditis",
      "aneurysm",
      "aortic dissection",
      "high blood pressure",
      "low blood pressure",
      "hypotension",
      "atherosclerosis",
      "peripheral artery disease",
      "varicose veins",
      "deep vein thrombosis",
      "DVT",

      // Neurological Conditions
      "seizure",
      "epilepsy",
      "multiple sclerosis",
      "MS",
      "Parkinson's disease",
      "Alzheimer's disease",
      "dementia",
      "concussion",
      "traumatic brain injury",
      "TBI",
      "meningitis",
      "encephalitis",
      "neuralgia",
      "neuropathy",
      "peripheral neuropathy",
      "carpal tunnel syndrome",
      "bell's palsy",
      "vertigo",
      "dizziness",
      "syncope",
      "fainting",
      "amyotrophic lateral sclerosis",
      "ALS",
      "Lou Gehrig's disease",
      "Huntington's disease",
      "myasthenia gravis",
      "narcolepsy",
      "cataplexy",

      // Gastrointestinal Conditions
      "gastroesophageal reflux disease",
      "GERD",
      "acid reflux",
      "heartburn",
      "ulcer",
      "gastric ulcer",
      "peptic ulcer",
      "gastritis",
      "stomach flu",
      "gastroenteritis",
      "food poisoning",
      "irritable bowel syndrome",
      "IBS",
      "inflammatory bowel disease",
      "IBD",
      "Crohn's disease",
      "ulcerative colitis",
      "celiac disease",
      "diverticulitis",
      "diverticulosis",
      "appendicitis",
      "pancreatitis",
      "gallstones",
      "cholecystitis",
      "hepatitis",
      "cirrhosis",
      "fatty liver disease",
      "jaundice",
      "hemorrhoids",
      "constipation",
      "diarrhea",
      "dysentery",
      "nausea",
      "vomiting",

      // Metabolic & Endocrine Conditions
      "hypothyroidism",
      "hyperthyroidism",
      "Graves' disease",
      "Hashimoto's thyroiditis",
      "goiter",
      "Cushing's syndrome",
      "Addison's disease",
      "type 1 diabetes",
      "type 2 diabetes",
      "gestational diabetes",
      "metabolic syndrome",
      "hypoglycemia",
      "hyperglycemia",
      "high cholesterol",
      "hypercholesterolemia",
      "high triglycerides",
      "hypertriglyceridemia",
      "gout",
      "osteoporosis",
      "rickets",
      "vitamin D deficiency",
      "iron deficiency",
      "anemia",
      "iron deficiency anemia",
      "pernicious anemia",
      "hemochromatosis",
      "polycystic ovary syndrome",
      "PCOS",
      "hypothalamic amenorrhea",

      // Musculoskeletal Conditions
      "osteoarthritis",
      "rheumatoid arthritis",
      "psoriatic arthritis",
      "juvenile arthritis",
      "gout",
      "lupus",
      "fibromyalgia",
      "ankylosing spondylitis",
      "scoliosis",
      "kyphosis",
      "lordosis",
      "herniated disc",
      "slipped disc",
      "sciatica",
      "bursitis",
      "tendinitis",
      "tendinosis",
      "tenosynovitis",
      "rotator cuff tear",
      "frozen shoulder",
      "adhesive capsulitis",
      "tennis elbow",
      "golfer's elbow",
      "carpal tunnel syndrome",
      "plantar fasciitis",
      "shin splints",
      "stress fracture",
      "osteopenia",
      "osteoporosis",
      "bone spur",
      "muscle strain",
      "muscle tear",
      "ligament sprain",
      "ligament tear",
      "dislocation",
      "subluxation",

      // Skin Conditions
      "acne",
      "eczema",
      "dermatitis",
      "contact dermatitis",
      "atopic dermatitis",
      "seborrheic dermatitis",
      "psoriasis",
      "rosacea",
      "hives",
      "urticaria",
      "shingles",
      "herpes zoster",
      "cold sore",
      "herpes simplex",
      "ringworm",
      "athlete's foot",
      "jock itch",
      "fungal infection",
      "impetigo",
      "cellulitis",
      "abscess",
      "boil",
      "carbuncle",
      "wart",
      "mole",
      "melanoma",
      "basal cell carcinoma",
      "squamous cell carcinoma",
      "vitiligo",
      "alopecia",
      "hair loss",
      "dandruff",
      "seborrheic keratosis",
      "skin tag",
      "rash",
      "blister",
      "burn",
      "sunburn",
      "frostbite",

      // Eye & Vision Conditions
      "nearsightedness",
      "myopia",
      "farsightedness",
      "hyperopia",
      "astigmatism",
      "presbyopia",
      "amblyopia",
      "lazy eye",
      "strabismus",
      "crossed eyes",
      "color blindness",
      "color vision deficiency",
      "cataract",
      "glaucoma",
      "macular degeneration",
      "retinal detachment",
      "retinopathy",
      "diabetic retinopathy",
      "dry eye syndrome",
      "conjunctivitis",
      "pink eye",
      "sty",
      "chalazion",
      "blepharitis",
      "corneal ulcer",
      "corneal abrasion",
      "uveitis",
      "optic neuritis",
      "papilledema",
      "scotoma",
      "blindness",
      "legal blindness",
      "photophobia",
      "light sensitivity",

      // Ear & Hearing Conditions
      "hearing loss",
      "deafness",
      "tinnitus",
      "ringing in ears",
      "Meniere's disease",
      "labyrinthitis",
      "acoustic neuroma",
      "ear infection",
      "otitis media",
      "otitis externa",
      "swimmer's ear",
      "earwax buildup",
      "cerumen impaction",
      "perforated eardrum",
      "ruptured eardrum",
      "otosclerosis",
      "eustachian tube dysfunction",

      // Mental Health Conditions
      "generalized anxiety disorder",
      "GAD",
      "panic disorder",
      "panic attack",
      "social anxiety disorder",
      "specific phobia",
      "agoraphobia",
      "obsessive-compulsive disorder",
      "OCD",
      "post-traumatic stress disorder",
      "PTSD",
      "major depressive disorder",
      "MDD",
      "persistent depressive disorder",
      "dysthymia",
      "bipolar disorder",
      "cyclothymia",
      "seasonal affective disorder",
      "SAD",
      "postpartum depression",
      "premenstrual dysphoric disorder",
      "PMDD",
      "schizophrenia",
      "schizoaffective disorder",
      "psychosis",
      "hallucination",
      "delusion",
      "dissociative identity disorder",
      "DID",
      "multiple personality disorder",
      "borderline personality disorder",
      "antisocial personality disorder",
      "narcissistic personality disorder",
      "histrionic personality disorder",
      "avoidant personality disorder",
      "dependent personality disorder",
      "anorexia nervosa",
      "bulimia nervosa",
      "binge eating disorder",
      "attention deficit hyperactivity disorder",
      "ADHD",
      "autism spectrum disorder",
      "ASD",
      "Asperger's syndrome",
      "Tourette syndrome",
      "tic disorder",
      "addiction",
      "substance use disorder",
      "alcoholism",
      "alcohol use disorder",

      // Infectious Diseases
      "common cold",
      "influenza",
      "COVID-19",
      "coronavirus",
      "SARS",
      "MERS",
      "measles",
      "mumps",
      "rubella",
      "chickenpox",
      "varicella",
      "shingles",
      "herpes zoster",
      "smallpox",
      "polio",
      "diphtheria",
      "tetanus",
      "pertussis",
      "whooping cough",
      "scarlet fever",
      "rheumatic fever",
      "malaria",
      "dengue fever",
      "yellow fever",
      "Zika virus",
      "Ebola",
      "HIV",
      "AIDS",
      "hepatitis A",
      "hepatitis B",
      "hepatitis C",
      "tuberculosis",
      "TB",
      "typhoid fever",
      "cholera",
      "dysentery",
      "E. coli infection",
      "salmonella",
      "food poisoning",
      "strep throat",
      "streptococcal infection",
      "staph infection",
      "staphylococcal infection",
      "MRSA",
      "meningitis",
      "encephalitis",
      "rabies",
      "Lyme disease",
      "Rocky Mountain spotted fever",
      "trichinosis",
      "hookworm",
      "roundworm",
      "tapeworm",
      "pinworm",
      "scabies",
      "lice",
      "chlamydia",
      "gonorrhea",
      "syphilis",
      "herpes",
      "HPV",
      "human papillomavirus",
      "pneumonia",
      "bronchitis",
      "sinus infection",
      "sinusitis",
      "urinary tract infection",
      "UTI",

      // Autoimmune Conditions
      "rheumatoid arthritis",
      "lupus",
      "systemic lupus erythematosus",
      "SLE",
      "multiple sclerosis",
      "MS",
      "type 1 diabetes",
      "Hashimoto's thyroiditis",
      "Graves' disease",
      "psoriasis",
      "psoriatic arthritis",
      "ankylosing spondylitis",
      "inflammatory bowel disease",
      "IBD",
      "Crohn's disease",
      "ulcerative colitis",
      "celiac disease",
      "Addison's disease",
      "Sjögren's syndrome",
      "vasculitis",
      "myasthenia gravis",
      "Guillain-Barré syndrome",
      "GBS",
      "alopecia areata",
      "vitiligo",
      "scleroderma",
      "polymyalgia rheumatica",
      "giant cell arteritis",
      "temporal arteritis",

      // Cancer Types
      "lung cancer",
      "breast cancer",
      "colorectal cancer",
      "prostate cancer",
      "skin cancer",
      "melanoma",
      "leukemia",
      "lymphoma",
      "Hodgkin's lymphoma",
      "non-Hodgkin's lymphoma",
      "multiple myeloma",
      "pancreatic cancer",
      "ovarian cancer",
      "cervical cancer",
      "uterine cancer",
      "endometrial cancer",
      "testicular cancer",
      "bladder cancer",
      "kidney cancer",
      "renal cell carcinoma",
      "liver cancer",
      "hepatocellular carcinoma",
      "stomach cancer",
      "esophageal cancer",
      "thyroid cancer",
      "brain tumor",
      "glioma",
      "glioblastoma",
      "meningioma",
      "pituitary tumor",
      "bone cancer",
      "osteosarcoma",
      "soft tissue sarcoma",
      "carcinoma",
      "adenocarcinoma",
      "squamous cell carcinoma",
      "basal cell carcinoma",

      // Reproductive & Urinary Conditions
      "urinary tract infection",
      "UTI",
      "kidney infection",
      "pyelonephritis",
      "kidney stone",
      "nephrolithiasis",
      "kidney disease",
      "chronic kidney disease",
      "CKD",
      "renal failure",
      "polycystic kidney disease",
      "PKD",
      "urinary incontinence",
      "overactive bladder",
      "interstitial cystitis",
      "painful bladder syndrome",
      "benign prostatic hyperplasia",
      "BPH",
      "enlarged prostate",
      "prostatitis",
      "erectile dysfunction",
      "ED",
      "premature ejaculation",
      "testicular torsion",
      "epididymitis",
      "orchitis",
      "varicocele",
      "hydrocele",
      "endometriosis",
      "adenomyosis",
      "uterine fibroids",
      "ovarian cyst",
      "polycystic ovary syndrome",
      "PCOS",
      "premenstrual syndrome",
      "PMS",
      "premenstrual dysphoric disorder",
      "PMDD",
      "dysmenorrhea",
      "painful periods",
      "amenorrhea",
      "absent periods",
      "menorrhagia",
      "heavy periods",
      "vaginitis",
      "bacterial vaginosis",
      "BV",
      "yeast infection",
      "candidiasis",
      "pelvic inflammatory disease",
      "PID",
      "cervicitis",
      "sexually transmitted infection",
      "STI",
      "sexually transmitted disease",
      "STD",

      // Pregnancy & Childbirth Conditions
      "morning sickness",
      "hyperemesis gravidarum",
      "gestational diabetes",
      "preeclampsia",
      "eclampsia",
      "placenta previa",
      "placental abruption",
      "preterm labor",
      "premature birth",
      "ectopic pregnancy",
      "miscarriage",
      "stillbirth",
      "birth defect",
      "congenital disorder",
      "postpartum depression",
      "postpartum hemorrhage",
      "mastitis",
    ],
    languages: [
      "english",
      "spanish",
      "french",
      "german",
      "italian",
      "portuguese",
      "russian",
      "chinese",
      "japanese",
      "korean",
      "arabic",
      "hindi",
      "bengali",
      "punjabi",
      "dutch",
      "swedish",
      "norwegian",
      "danish",
      "albanian",
      "armenian",
      "basque",
      "belarusian",
      "bosnian",
      "bulgarian",
      "catalan",
      "croatian",
      "czech",
      "danish",
      "dutch",
      "english",
      "estonian",
      "faroese",
      "finnish",
      "flemish",
      "french",
      "frisian",
      "galician",
      "georgian",
      "german",
      "greek",
      "hungarian",
      "icelandic",
      "irish",
      "italian",
      "latvian",
      "lithuanian",
      "luxembourgish",
      "macedonian",
      "maltese",
      "moldovan",
      "montenegrin",
      "norwegian",
      "occitan",
      "polish",
      "portuguese",
      "romanian",
      "romansh",
      "russian",
      "sami",
      "scottish gaelic",
      "serbian",
      "slovak",
      "slovenian",
      "spanish",
      "swedish",
      "ukrainian",
      "welsh",
      "yiddish",
      "finnish",
      "greek",
      "turkish",
      "hebrew",
      "polish",
      "czech",
      "arabic",
      "armenian",
      "assamese",
      "azerbaijani",
      "bahasa indonesia",
      "bahasa malaysia",
      "bengali",
      "burmese",
      "cebuano",
      "dari",
      "filipino",
      "gujarati",
      "hebrew",
      "hindi",
      "hmong",
      "japanese",
      "javanese",
      "kannada",
      "kazakh",
      "khmer",
      "korean",
      "kurdish",
      "lao",
      "malay",
      "malayalam",
      "mandarin",
      "cantonese",
      "wu",
      "hakka",
      "min",
      "gan",
      "marathi",
      "mongolian",
      "nepali",
      "odia",
      "oriya",
      "pashto",
      "persian",
      "farsi",
      "punjabi",
      "sanskrit",
      "sindhi",
      "sinhala",
      "sinhalese",
      "tagalog",
      "tajik",
      "tamil",
      "telugu",
      "thai",
      "tibetan",
      "turkish",
      "turkmen",
      "urdu",
      "uyghur",
      "uzbek",
      "corsican",
      "franco-provençal",
      "ladino",
      "sardinian",
      "breton",
      "manx",
      "gaelic",
      "galician",
      "bavarian",
      "silesian",

      // Slavic & Central
      "chechen",
      "tat",
      "bashkir",
      "chuvash",
      "komi",
      "marie",
      "tatar",
      "ossetian",

      // African
      "swahili",
      "zulu",
      "xhosa",
      "afrikaans",
      "somali",
      "amharic",
      "tigrinya",
      "hausa",
      "yoruba",
      "igbo",
      "shona",
      "lingala",
      "kikuyu",
      "berber",
      "wolof",
      "malagasy",

      // Indigenous / Endangered
      "nahuatl",
      "quechua",
      "aymara",
      "guarani",
      "mapudungun",
      "cherokee",
      "navajo",
      "inuktitut",
      "maori",
      "sami",
      "inuinnaqtun",
      "inuptiaq",
      "māori",

      // East & Southeast Asia
      "ilokano",
      "hiligaynon",
      "waray",
      "banjar",
      "sundanese",
      "bikol",
      "tagbanwa",
      "balinese",
      "acehnese",

      // Middle Eastern & Central Asia
      "tajik",
      "azerbaijani",
      "tatar",
      "uzbek",
      "kazakh",
      "kirghiz",
      "turkmen",
      "chechen",
      "ossetian",
      "kurdish",
      "sorani",
      "kurmanji",

      // Indian Subcontinent (additions)
      "bodo",
      "maithili",
      "dogri",
      "kashmiri",
      "santali",
      "manipuri",
      "meitei",
      "konkani",
      "tulu",

      // Constructed & Classical
      "esperanto",
      "latin",
      "sanskrit",
      "volapük",
      "interlingua",
      "lojban",
      "klingon",
      "elvish",
      "dothraki",
      "valyrian",

      // Sign Languages
      "american sign language",
      "british sign language",
      "australian sign language",
      "french sign language",
      "japanese sign language",
      "international sign",
      "vietnamese",
      "hungarian",
      "thai",
      "vietnamese",
    ],
    emotions: [
      "happy",
      "sad",
      "angry",
      "amused",
      "blissful",
      "cheerful",
      "disappointed",
      "discouraged",
      "disheartened",
      "dejected",
      "despondent",
      "down",
      "downcast",
      "downhearted",
      "delighted",
      "ecstatic",
      "elated",
      "euphoric",
      "exhilarated",
      "giddy",
      "glad",
      "gleeful",
      "jubilant",
      "merry",
      "pleased",
      "thrilled",
      "overjoyed",
      "satisfied",
      "silly",
      "buoyant",
      "lighthearted",
      "upbeat",
      "optimistic",
      "playful",
      "carefree",
      "contented",
      "serene",
      "tranquil",
      "fulfilled",
      "gratified",
      "relieved",
      "thankful",
      "appreciated",
      "valued",
      "triumphant",
      "victorious",
      "accomplished",
      "fearful",
      "surprised",
      "disgusted",
      "joyful",
      "excited",
      "content",
      "peaceful",
      "anxious",
      "worried",
      "stressed",
      "relaxed",
      "calm",
      "bored",
      "interested",
      "curious",
      "confused",
      "frustrated",
      "jealous",
      "envious",
      "proud",
      "adoring",
      "affectionate",
      "content",
      "enthusiastic",
      "inspired",
      "motivated",
      "passionate",
      "warm",
      "touched",
      "loving",
      "caring",
      "hopeful",
      "grateful",
      "appreciative",
      "trusting",
      "relieved",

      // Negative - nuanced
      "resentful",
      "irritated",
      "agitated",
      "annoyed",
      "embarrassed",
      "ashamed",
      "guilty",
      "lonely",
      "isolated",
      "overwhelmed",
      "helpless",
      "apathetic",
      "numb",
      "worried",
      "fearful",
      "horrified",
      "devastated",
      "betrayed",
      "rejected",
      "insecure",

      // Mixed/complex
      "nostalgic",
      "bittersweet",
      "ambivalent",
      "uncertain",
      "conflicted",
      "resigned",
      "surprised",
      "startled",
      "puzzled",
      "indecisive",

      // Social/Relational
      "awkward",
      "connected",
      "disconnected",
      "excluded",
      "valued",
      "envied",
      "admired",
      "resented",
      "mocked",
      "accepted",
      "validated",
      "appreciated",

      // High-intensity
      "ecstatic",
      "rageful",
      "panicked",
      "euphoric",
      "elated",
      "exasperated",
      "furious",
      "hysterical",
      "terrified",

      // Low-intensity / mood
      "moody",
      "gloomy",
      "blue",
      "irritable",
      "tired",
      "lethargic",
      "drained",
      "mellow",
      "pensive",
      "dreamy",
      "ashamed",
      "guilty",
      "grateful",
      "hopeful",
    ],
    "historical figures": [
      "washington",
      "lincoln",
      "roosevelt",
      "churchill",
      "gandhi",
      "mandela",
      "king",
      "einstein",
      "newton",
      "darwin",
      "galileo",
      "aristotle",
      "plato",
      "socrates",
      "caesar",
      "alexander",
      "napoleon",
      "cleopatra",
      "columbus",
      "leonardo",
      "michelangelo",
      "shakespeare",
      "mozart",
      "beethoven",
      "tesla",
      "edison",
      "curie",
      "nightingale",
      "tubman",
      "julius caesar",
      "nero",
      "attila",
      "genghis khan",
      "charlemagne",
      "fdr",
      "stalin",
      "hitler",
      "mao",
      "truman",
      "reagan",
      "thatcher",
      "khrushchev",
      "lincoln",
      "grant",
      "bismarck",
      "tito",
      "mussolini",
      "castro",
      "de gaulle",

      // Revolutionaries & activists
      "che guevara",
      "emmaline pankhurst",
      "malcolm x",
      "susan b anthony",
      "betty friedan",
      "frederick douglass",
      "sojourner truth",
      "ida b wells",
      "john brown",
      "cesar chavez",
      "desmond tutu",
      "gloria steinem",

      // Scientists & inventors
      "bohr",
      "heisenberg",
      "marie curie",
      "pasteur",
      "louis pasteur",
      "hawking",
      "franklin",
      "james watt",
      "da vinci",
      "alan turing",
      "carver",
      "fleming",
      "sagan",

      // Explorers
      "magellan",
      "vasco da gama",
      "amerigo vespucci",
      "leif erikson",
      "marco polo",
      "zheng he",
      "cook",
      "amundsen",
      "nansen",

      // Artists & writers
      "dante",
      "homer",
      "chopin",
      "bach",
      "verdi",
      "rembrandt",
      "van gogh",
      "goethe",
      "emily dickinson",
      "jane austen",
      "tolstoy",
      "dostoevsky",
      "mark twain",
      "orwell",
      "thoreau",
      "whitman",

      // Philosophers
      "confucius",
      "laozi",
      "descartes",
      "voltaire",
      "kant",
      "hume",
      "locke",
      "hobbes",
      "hegel",
      "nietzsche",

      // Religious figures
      "jesus",
      "muhammad",
      "buddha",
      "moses",
      "abraham",
      "confucius",
      "zoroaster",
      "joan of arc",
      "martin luther",
      "calvin",

      // Royals
      "elizabeth i",
      "victoria",
      "henry viii",
      "george iii",
      "peter the great",
      "catherine the great",
      "akbar",
      "philip ii",

      // Native & Indigenous leaders
      "crazy horse",
      "sitting bull",
      "tecumseh",
      "geronimo",
      "pocahontas",

      // Misc. iconic
      "amelia earhart",
      "neil armstrong",
      "yuri gagarin",
      "rosa luxemburg",
      "grace hopper",
      "anne frank",
      "alexander graham bell",
      "wright brothers",
      "parks",
    ],
  };

  const semanticGroups = {
    historical_figures: {
      us_presidents: [
        "washington",
        "lincoln",
        "roosevelt",
        "fdr",
        "truman",
        "reagan",
        "grant",
      ],

      political_leaders: [
        "churchill",
        "gandhi",
        "mandela",
        "mao",
        "hitler",
        "stalin",
        "thatcher",
        "khrushchev",
        "bismarck",
        "napoleon",
        "caesar",
        "alexander",
        "de gaulle",
        "castro",
        "tito",
        "mussolini",
        "charlemagne",
        "genghis khan",
      ],

      revolutionaries_activists: [
        "king",
        "tubman",
        "parks",
        "che guevara",
        "malcolm x",
        "betty friedan",
        "susan b anthony",
        "emmaline pankhurst",
        "gloria steinem",
        "cesar chavez",
        "frederick douglass",
        "sojourner truth",
        "john brown",
        "desmond tutu",
        "ida b wells",
      ],

      scientists_inventors: [
        "einstein",
        "newton",
        "darwin",
        "galileo",
        "tesla",
        "edison",
        "curie",
        "marie curie",
        "pasteur",
        "louis pasteur",
        "bohr",
        "heisenberg",
        "hawking",
        "alan turing",
        "carver",
        "franklin",
        "james watt",
        "sagan",
        "wright brothers",
        "alexander graham bell",
      ],

      artists_writers: [
        "leonardo",
        "michelangelo",
        "shakespeare",
        "mozart",
        "beethoven",
        "chopin",
        "bach",
        "verdi",
        "rembrandt",
        "van gogh",
        "dante",
        "homer",
        "emily dickinson",
        "jane austen",
        "goethe",
        "tolstoy",
        "dostoevsky",
        "orwell",
        "mark twain",
        "thoreau",
        "whitman",
      ],

      philosophers: [
        "aristotle",
        "plato",
        "socrates",
        "descartes",
        "kant",
        "voltaire",
        "locke",
        "hobbes",
        "hegel",
        "hume",
        "confucius",
        "laozi",
        "nietzsche",
      ],

      religious_figures: [
        "jesus",
        "muhammad",
        "buddha",
        "moses",
        "abraham",
        "joan of arc",
        "martin luther",
        "calvin",
        "zoroaster",
      ],

      royalty_monarchs: [
        "elizabeth i",
        "victoria",
        "cleopatra",
        "henry viii",
        "george iii",
        "peter the great",
        "catherine the great",
        "akbar",
        "philip ii",
      ],

      explorers: [
        "columbus",
        "magellan",
        "vasco da gama",
        "amerigo vespucci",
        "leif erikson",
        "marco polo",
        "zheng he",
        "cook",
        "amundsen",
        "nansen",
      ],

      indigenous_leaders: [
        "sitting bull",
        "crazy horse",
        "tecumseh",
        "geronimo",
        "pocahontas",
      ],

      modern_icons_misc: [
        "amelia earhart",
        "neil armstrong",
        "yuri gagarin",
        "grace hopper",
        "anne frank",
        "rosa luxemburg",
      ],
    },
    emotions: {
      positive_emotions: [
        "happy",
        "joyful",
        "ecstatic",
        "elated",
        "euphoric",
        "blissful",
        "cheerful",
        "delighted",
        "thrilled",
        "overjoyed",
        "gleeful",
        "excited",
        "glad",
        "jubilant",
        "merry",
        "pleased",
        "satisfied",
        "triumphant",
        "victorious",
        "accomplished",
        "adoring",
        "affectionate",
        "content",
        "enthusiastic",
        "inspired",
        "motivated",
        "passionate",
        "warm",
        "touched",
        "loving",
        "caring",
        "hopeful",
        "grateful",
        "appreciative",
        "trusting",
        "thankful",
        "appreciated",
        "valued",
      ],

      negative_emotions: [
        "sad",
        "angry",
        "frustrated",
        "disappointed",
        "discouraged",
        "dejected",
        "disheartened",
        "despondent",
        "down",
        "downcast",
        "lonely",
        "isolated",
        "helpless",
        "hopeless",
        "overwhelmed",
        "worried",
        "anxious",
        "fearful",
        "guilty",
        "ashamed",
        "resentful",
        "irritated",
        "agitated",
        "annoyed",
        "rejected",
        "betrayed",
        "insecure",
        "moody",
        "blue",
        "gloomy",
        "numb",
        "horrified",
        "devastated",
      ],

      calming_emotions: [
        "relaxed",
        "calm",
        "serene",
        "tranquil",
        "content",
        "peaceful",
        "mellow",
        "pensive",
        "lighthearted",
        "fulfilled",
      ],

      social_emotions: [
        "connected",
        "disconnected",
        "valued",
        "excluded",
        "envied",
        "admired",
        "mocked",
        "appreciated",
        "validated",
        "trusted",
        "accepted",
        "awkward",
      ],

      high_intensity_emotions: [
        "ecstatic",
        "elated",
        "exhilarated",
        "hysterical",
        "furious",
        "panicked",
        "terrified",
        "rageful",
        "enthusiastic",
        "giddy",
        "inspired",
        "passionate",
      ],

      low_intensity_emotions: [
        "bored",
        "apathetic",
        "drained",
        "lethargic",
        "tired",
        "resigned",
        "dreamy",
      ],

      complex_emotions: [
        "nostalgic",
        "bittersweet",
        "ambivalent",
        "uncertain",
        "conflicted",
        "startled",
        "puzzled",
        "indecisive",
      ],

      affiliative_emotions: [
        "adoring",
        "affectionate",
        "loving",
        "caring",
        "warm",
        "touched",
        "grateful",
        "thankful",
        "appreciative",
        "hopeful",
        "motivated",
        "trusting",
      ],

      pride_shame_emotions: [
        "proud",
        "accomplished",
        "ashamed",
        "guilty",
        "embarrassed",
      ],

      playful_emotions: [
        "playful",
        "silly",
        "amused",
        "curious",
        "interested",
        "upbeat",
        "buoyant",
        "carefree",
      ],
    },
    medical_conditions: {
      infectious_diseases: [
        "cold",
        "flu",
        "COVID-19",
        "tuberculosis",
        "malaria",
        "measles",
        "mumps",
        "rubella",
        "chickenpox",
        "HIV",
        "AIDS",
        "hepatitis A",
        "hepatitis B",
        "hepatitis C",
        "strep throat",
        "MRSA",
        "SARS",
        "MERS",
        "E. coli infection",
        "salmonella",
        "dysentery",
        "cholera",
        "scarlet fever",
        "rabies",
        "Lyme disease",
        "typhoid fever",
        "Zika virus",
        "Ebola",
      ],

      respiratory_disorders: [
        "asthma",
        "bronchitis",
        "emphysema",
        "COPD",
        "sleep apnea",
        "pneumonia",
        "sinusitis",
        "rhinitis",
        "laryngitis",
        "croup",
        "whooping cough",
        "RSV",
        "pulmonary fibrosis",
        "pulmonary embolism",
        "tuberculosis",
      ],

      cardiovascular_disorders: [
        "heart disease",
        "heart attack",
        "stroke",
        "hypertension",
        "high blood pressure",
        "low blood pressure",
        "arrhythmia",
        "atrial fibrillation",
        "coronary artery disease",
        "angina",
        "congestive heart failure",
        "aneurysm",
        "DVT",
        "varicose veins",
      ],

      mental_health_disorders: [
        "depression",
        "anxiety",
        "panic attack",
        "bipolar disorder",
        "schizophrenia",
        "OCD",
        "PTSD",
        "ADHD",
        "autism",
        "dissociative identity disorder",
        "borderline personality disorder",
        "narcissistic personality disorder",
        "eating disorder",
        "anorexia",
        "bulimia",
        "addiction",
        "substance use disorder",
      ],

      neurological_disorders: [
        "epilepsy",
        "seizure",
        "Parkinson's disease",
        "Alzheimer's disease",
        "multiple sclerosis",
        "ALS",
        "TBI",
        "concussion",
        "neuropathy",
        "carpal tunnel syndrome",
        "vertigo",
        "fainting",
        "migraine",
        "dementia",
        "encephalitis",
        "meningitis",
      ],

      gastrointestinal_disorders: [
        "GERD",
        "ulcer",
        "gastritis",
        "IBS",
        "IBD",
        "Crohn's disease",
        "ulcerative colitis",
        "appendicitis",
        "hepatitis",
        "gallstones",
        "constipation",
        "diarrhea",
        "nausea",
        "vomiting",
        "pancreatitis",
      ],

      metabolic_endocrine_disorders: [
        "diabetes",
        "type 1 diabetes",
        "type 2 diabetes",
        "gestational diabetes",
        "hypoglycemia",
        "hyperglycemia",
        "thyroid disorder",
        "hypothyroidism",
        "hyperthyroidism",
        "PCOS",
        "Cushing's syndrome",
        "Addison's disease",
        "high cholesterol",
        "gout",
        "osteoporosis",
      ],

      musculoskeletal_disorders: [
        "arthritis",
        "rheumatoid arthritis",
        "osteoarthritis",
        "fibromyalgia",
        "gout",
        "scoliosis",
        "herniated disc",
        "sciatica",
        "tendinitis",
        "bursitis",
        "muscle strain",
        "fracture",
        "sprain",
        "dislocation",
        "shin splints",
      ],

      autoimmune_conditions: [
        "lupus",
        "MS",
        "Hashimoto's thyroiditis",
        "Graves' disease",
        "type 1 diabetes",
        "psoriasis",
        "celiac disease",
        "Addison's disease",
        "IBD",
        "myasthenia gravis",
        "scleroderma",
        "alopecia areata",
        "vitiligo",
      ],

      cancers: [
        "breast cancer",
        "lung cancer",
        "skin cancer",
        "prostate cancer",
        "colorectal cancer",
        "leukemia",
        "lymphoma",
        "melanoma",
        "pancreatic cancer",
        "ovarian cancer",
        "brain tumor",
        "thyroid cancer",
      ],

      reproductive_urinary_disorders: [
        "UTI",
        "kidney stone",
        "prostatitis",
        "erectile dysfunction",
        "enlarged prostate",
        "endometriosis",
        "PCOS",
        "PMS",
        "PMDD",
        "vaginitis",
        "yeast infection",
        "STD",
        "STI",
      ],

      pregnancy_childbirth_conditions: [
        "gestational diabetes",
        "preeclampsia",
        "eclampsia",
        "preterm labor",
        "placenta previa",
        "miscarriage",
        "stillbirth",
        "postpartum depression",
        "morning sickness",
        "hyperemesis gravidarum",
      ],

      dermatological_conditions: [
        "acne",
        "eczema",
        "dermatitis",
        "psoriasis",
        "rosacea",
        "shingles",
        "hives",
        "ringworm",
        "cellulitis",
        "melanoma",
        "skin tag",
        "mole",
        "vitiligo",
        "alopecia",
        "dandruff",
        "rash",
        "burn",
      ],

      eye_conditions: [
        "myopia",
        "hyperopia",
        "astigmatism",
        "presbyopia",
        "cataract",
        "glaucoma",
        "conjunctivitis",
        "pink eye",
        "macular degeneration",
        "sty",
        "blindness",
      ],

      ear_conditions: [
        "hearing loss",
        "tinnitus",
        "ear infection",
        "swimmer's ear",
        "otosclerosis",
        "Meniere's disease",
        "ruptured eardrum",
      ],

      nutritional_deficiencies: [
        "iron deficiency",
        "anemia",
        "vitamin D deficiency",
        "rickets",
        "malnutrition",
        "scurvy",
        "osteoporosis",
        "pernicious anemia",
      ],

      sleep_disorders: [
        "insomnia",
        "sleep apnea",
        "narcolepsy",
        "restless legs syndrome",
        "cataplexy",
        "parasomnia",
        "night terrors",
      ],
    },
    languages: {
      romance_languages: [
        "spanish",
        "french",
        "italian",
        "portuguese",
        "romanian",
        "catalan",
        "occitan",
        "galician",
        "corsican",
        "sardinian",
        "ladino",
        "franco-provençal",
      ],

      germanic_languages: [
        "english",
        "german",
        "dutch",
        "flemish",
        "swedish",
        "norwegian",
        "danish",
        "icelandic",
        "luxembourgish",
        "afrikaans",
        "frisian",
        "bavarian",
        "silesian",
      ],

      slavic_languages: [
        "russian",
        "ukrainian",
        "polish",
        "czech",
        "slovak",
        "slovenian",
        "serbian",
        "croatian",
        "bosnian",
        "macedonian",
        "bulgarian",
        "belarusian",
        "montenegrin",
      ],

      baltic_languages: ["latvian", "lithuanian"],

      celtic_languages: [
        "irish",
        "welsh",
        "scottish gaelic",
        "manx",
        "breton",
        "gaelic",
      ],

      finno_ugric_languages: [
        "finnish",
        "estonian",
        "sami",
        "hungarian",
        "komi",
        "marie",
      ],

      indo_aryan_languages: [
        "hindi",
        "urdu",
        "bengali",
        "punjabi",
        "marathi",
        "gujarati",
        "sanskrit",
        "assamese",
        "odia",
        "oriya",
        "kashmiri",
        "maithili",
        "dogri",
        "bodo",
        "konkani",
        "sindhi",
      ],

      dravidian_languages: ["tamil", "telugu", "kannada", "malayalam", "tulu"],

      iranian_languages: [
        "persian",
        "farsi",
        "kurdish",
        "pashto",
        "dari",
        "tajik",
        "sorani",
        "kurmanji",
      ],

      turkic_languages: [
        "turkish",
        "azerbaijani",
        "kazakh",
        "uzbek",
        "turkmen",
        "tatar",
        "bashkir",
        "chuvash",
      ],

      east_asian_languages: [
        "chinese",
        "mandarin",
        "cantonese",
        "wu",
        "gan",
        "min",
        "hakka",
        "japanese",
        "korean",
        "mongolian",
        "tibetan",
      ],

      southeast_asian_languages: [
        "thai",
        "burmese",
        "khmer",
        "lao",
        "tagalog",
        "filipino",
        "cebuano",
        "ilokano",
        "hiligaynon",
        "waray",
        "bikol",
        "sundanese",
        "javanese",
        "balinese",
        "banjar",
        "acehnese",
        "malay",
        "bahasa indonesia",
        "bahasa malaysia",
      ],

      semitic_languages: ["arabic", "hebrew", "amharic", "tigrinya", "maltese"],

      caucasian_languages: ["georgian", "armenian", "chechen", "ossetian"],

      indigenous_languages: [
        "navajo",
        "cherokee",
        "nahuatl",
        "quechua",
        "aymara",
        "guarani",
        "mapudungun",
        "inuktitut",
        "inuinnaqtun",
        "inuptiaq",
        "maori",
        "māori",
      ],

      african_languages: [
        "swahili",
        "zulu",
        "xhosa",
        "somali",
        "hausa",
        "yoruba",
        "igbo",
        "shona",
        "lingala",
        "kikuyu",
        "berber",
        "wolof",
        "malagasy",
      ],

      constructed_languages: [
        "esperanto",
        "latin",
        "volapük",
        "interlingua",
        "lojban",
        "klingon",
        "elvish",
        "dothraki",
        "valyrian",
      ],

      sign_languages: [
        "american sign language",
        "british sign language",
        "australian sign language",
        "french sign language",
        "japanese sign language",
        "international sign",
      ],
    },
    animals: {
      canines: [
        "dog",
        "wolf",
        "fox",
        "coyote",
        "dingo",
        "jackal",
        "puppy",
        "husky",
        "german shepherd",
        "poodle",
        "labrador",
        "beagle",
        "collie",
        "pug",
        "bulldog",
        "terrier",
      ],
      felines: [
        "cat",
        "lion",
        "tiger",
        "leopard",
        "cheetah",
        "jaguar",
        "cougar",
        "panther",
        "kitten",
        "lynx",
        "ocelot",
        "bobcat",
        "puma",
        "house cat",
        "tabby",
        "siamese",
      ],
      rodents: [
        "mouse",
        "rat",
        "hamster",
        "gerbil",
        "guinea pig",
        "squirrel",
        "chipmunk",
        "beaver",
        "porcupine",
        "mole",
        "vole",
        "chinchilla",
        "capybara",
        "groundhog",
        "marmot",
        "prairie dog",
      ],
      farm_animals: [
        "cow",
        "pig",
        "horse",
        "chicken",
        "sheep",
        "goat",
        "donkey",
        "duck",
        "goose",
        "turkey",
        "bull",
        "ox",
        "calf",
        "lamb",
        "piglet",
        "foal",
        "pony",
        "mule",
      ],
      marine_mammals: [
        "whale",
        "dolphin",
        "porpoise",
        "orca",
        "seal",
        "sea lion",
        "walrus",
        "manatee",
        "dugong",
        "narwhal",
        "beluga",
      ],
      fish: [
        "salmon",
        "trout",
        "tuna",
        "bass",
        "cod",
        "goldfish",
        "shark",
        "stingray",
        "piranha",
        "angelfish",
        "swordfish",
        "marlin",
        "catfish",
        "carp",
        "eel",
        "sturgeon",
        "minnow",
      ],
      reptiles: [
        "snake",
        "lizard",
        "turtle",
        "crocodile",
        "alligator",
        "iguana",
        "chameleon",
        "gecko",
        "tortoise",
        "python",
        "cobra",
        "viper",
        "boa",
        "anaconda",
        "komodo dragon",
        "monitor lizard",
      ],
      amphibians: [
        "frog",
        "toad",
        "salamander",
        "newt",
        "axolotl",
        "caecilian",
        "treefrog",
        "bullfrog",
        "tadpole",
      ],
      birds: [
        "eagle",
        "hawk",
        "sparrow",
        "robin",
        "crow",
        "owl",
        "penguin",
        "ostrich",
        "flamingo",
        "parrot",
        "canary",
        "finch",
        "seagull",
        "pigeon",
        "dove",
        "hummingbird",
        "woodpecker",
        "peacock",
      ],
      insects: [
        "ant",
        "bee",
        "wasp",
        "butterfly",
        "moth",
        "beetle",
        "fly",
        "mosquito",
        "cockroach",
        "ladybug",
        "grasshopper",
        "cricket",
        "termite",
        "dragonfly",
        "mantis",
        "cicada",
        "firefly",
      ],
      arachnids: [
        "spider",
        "scorpion",
        "tick",
        "mite",
        "tarantula",
        "black widow",
        "daddy longlegs",
        "brown recluse",
      ],
      crustaceans: [
        "crab",
        "lobster",
        "shrimp",
        "crayfish",
        "prawn",
        "barnacle",
        "hermit crab",
        "krill",
      ],
      mollusks: [
        "snail",
        "slug",
        "octopus",
        "squid",
        "clam",
        "oyster",
        "mussel",
        "scallop",
        "nautilus",
        "cuttlefish",
      ],
      primates: [
        "monkey",
        "gorilla",
        "chimpanzee",
        "orangutan",
        "baboon",
        "gibbon",
        "lemur",
        "marmoset",
        "tamarin",
        "macaque",
        "bonobo",
        "mandrill",
      ],
      marsupials: [
        "kangaroo",
        "koala",
        "opossum",
        "wallaby",
        "wombat",
        "tasmanian devil",
        "quokka",
        "sugar glider",
        "numbat",
      ],
      bears: [
        "bear",
        "grizzly bear",
        "polar bear",
        "black bear",
        "panda",
        "brown bear",
        "koala bear",
        "teddy bear",
        "sun bear",
      ],
      large_herbivores: [
        "elephant",
        "giraffe",
        "rhinoceros",
        "hippopotamus",
        "buffalo",
        "bison",
        "moose",
        "elk",
        "deer",
        "antelope",
        "gazelle",
        "zebra",
        "camel",
        "llama",
        "alpaca",
      ],
      exotic_pets: [
        "ferret",
        "hedgehog",
        "sugar glider",
        "chinchilla",
        "tarantula",
        "iguana",
        "parrot",
        "macaw",
        "snake",
        "lizard",
        "turtle",
        "gecko",
        "axolotl",
      ],
    },
    clothing: {
      footwear: [
        "shoes",
        "boots",
        "sandals",
        "sneakers",
        "heels",
        "flip flops",
        "slippers",
        "loafers",
        "clogs",
        "moccasins",
        "oxfords",
        "pumps",
        "flats",
        "stilettos",
        "espadrilles",
        "wedges",
        "running shoes",
        "hiking boots",
        "rain boots",
        "snow boots",
        "work boots",
        "combat boots",
        "ankle boots",
        "platform shoes",
        "cleats",
        "ballet flats",
        "wingtips",
        "cowboy boots",
        "galoshes",
        "waders",
        "crocs",
      ],
      tops: [
        "shirt",
        "t-shirt",
        "blouse",
        "tank top",
        "polo",
        "sweatshirt",
        "sweater",
        "vest",
        "jersey",
        "tunic",
        "cardigan",
        "crop top",
        "halter top",
        "turtleneck",
        "v-neck",
        "crew neck",
        "henley",
        "flannel",
        "hoodie",
        "button-down",
        "dress shirt",
        "undershirt",
        "camisole",
        "tube top",
        "off-shoulder top",
        "bodysuit",
        "corset",
        "thermal",
        "long sleeve",
        "short sleeve",
        "sleeveless",
      ],
      bottoms: [
        "pants",
        "jeans",
        "shorts",
        "skirt",
        "trousers",
        "leggings",
        "sweatpants",
        "khakis",
        "chinos",
        "capris",
        "cargo pants",
        "dress pants",
        "slacks",
        "culottes",
        "joggers",
        "track pants",
        "yoga pants",
        "pajama pants",
        "bermuda shorts",
        "jean shorts",
        "board shorts",
        "boxers",
        "briefs",
        "miniskirt",
        "midi skirt",
        "maxi skirt",
        "pencil skirt",
        "a-line skirt",
        "pleated skirt",
        "tennis skirt",
        "corduroy pants",
        "palazzo pants",
        "bell bottoms",
        "overalls",
        "dungarees",
      ],
      dresses: [
        "dress",
        "gown",
        "sundress",
        "cocktail dress",
        "evening dress",
        "wedding dress",
        "mini dress",
        "midi dress",
        "maxi dress",
        "ball gown",
        "sheath dress",
        "shift dress",
        "wrap dress",
        "a-line dress",
        "bodycon dress",
        "slip dress",
        "jumper dress",
        "shirt dress",
        "blazer dress",
        "pinafore",
        "kaftan",
        "tunic dress",
        "sweater dress",
        "little black dress",
        "prom dress",
        "bridesmaid dress",
        "quinceañera dress",
      ],
      outerwear: [
        "jacket",
        "coat",
        "sweater",
        "hoodie",
        "blazer",
        "cardigan",
        "parka",
        "windbreaker",
        "trench coat",
        "overcoat",
        "peacoat",
        "duster",
        "poncho",
        "cape",
        "puffer jacket",
        "leather jacket",
        "bomber jacket",
        "jean jacket",
        "varsity jacket",
        "anorak",
        "raincoat",
        "shearling coat",
        "fur coat",
        "ski jacket",
        "track jacket",
        "suit jacket",
        "bolero",
        "fleece",
        "vest",
      ],
      formal_wear: [
        "suit",
        "tuxedo",
        "dress",
        "gown",
        "blazer",
        "dress shirt",
        "dress pants",
        "tie",
        "bow tie",
        "cummerbund",
        "vest",
        "dinner jacket",
        "formal coat",
        "tailcoat",
        "dress shoes",
        "evening bag",
        "cufflinks",
        "suspenders",
        "cocktail dress",
        "evening gown",
        "formal gloves",
        "shawl",
      ],
      athletic_wear: [
        "sweatshirt",
        "sweatpants",
        "tracksuit",
        "jersey",
        "shorts",
        "leggings",
        "tank top",
        "sports bra",
        "running shoes",
        "athletic socks",
        "headband",
        "wristband",
        "swimsuit",
        "swim trunks",
        "rash guard",
        "cycling shorts",
        "yoga pants",
        "compression shirt",
        "compression shorts",
        "windbreaker",
        "track jacket",
        "warm-up pants",
        "hoodie",
        "basketball shorts",
        "cleats",
        "golf shirt",
        "tennis skirt",
        "ski pants",
        "ski jacket",
      ],
      sleepwear: [
        "pajamas",
        "nightgown",
        "robe",
        "bathrobe",
        "slippers",
        "sleep shirt",
        "sleep pants",
        "nightshirt",
        "onesie",
        "pajama set",
        "camisole",
        "nightdress",
        "loungewear",
        "sleep shorts",
        "sleep mask",
        "sleeping cap",
      ],
      swimwear: [
        "swimsuit",
        "bikini",
        "swim trunks",
        "one-piece",
        "tankini",
        "board shorts",
        "rash guard",
        "swim shirt",
        "wetsuit",
        "swim briefs",
        "speedo",
        "bathing suit",
        "swim cap",
        "cover-up",
        "sarong",
      ],
      accessories: [
        "hat",
        "cap",
        "scarf",
        "gloves",
        "mittens",
        "belt",
        "tie",
        "bow tie",
        "watch",
        "jewelry",
        "sunglasses",
        "glasses",
        "headband",
        "earrings",
        "necklace",
        "bracelet",
        "ring",
        "anklet",
        "purse",
        "handbag",
        "backpack",
        "wallet",
        "clutch",
        "socks",
        "stockings",
        "tights",
        "pantyhose",
        "suspenders",
        "bandana",
        "handkerchief",
        "cufflinks",
        "pocket square",
        "umbrella",
      ],
      headwear: [
        "hat",
        "cap",
        "beanie",
        "beret",
        "fedora",
        "cowboy hat",
        "sun hat",
        "visor",
        "headband",
        "turban",
        "headscarf",
        "earmuffs",
        "helmet",
        "baseball cap",
        "snapback",
        "bucket hat",
        "fascinator",
        "bonnet",
        "crown",
        "tiara",
        "headwrap",
        "bandana",
        "hijab",
        "kippah",
        "turban",
      ],
      underwear: [
        "bra",
        "panties",
        "boxers",
        "briefs",
        "thong",
        "boyshorts",
        "undershirt",
        "camisole",
        "slip",
        "long johns",
        "thermal underwear",
        "lingerie",
        "boxer briefs",
        "trunk underwear",
        "g-string",
        "sports bra",
        "shapewear",
        "corset",
        "bustier",
        "garter belt",
        "underpants",
      ],
      winter_wear: [
        "sweater",
        "coat",
        "parka",
        "scarf",
        "gloves",
        "mittens",
        "beanie",
        "earmuffs",
        "thermal underwear",
        "snow pants",
        "snow boots",
        "winter jacket",
        "turtleneck",
        "wool socks",
        "fleece",
        "winter hat",
        "snow goggles",
        "snowsuit",
        "puffer jacket",
        "ski mask",
        "ear warmer",
        "leg warmers",
      ],
      seasonal: [
        "swimsuit",
        "sundress",
        "shorts",
        "winter coat",
        "bathing suit",
        "scarf",
        "sandals",
        "boots",
        "halloween costume",
        "christmas sweater",
        "rain boots",
        "raincoat",
        "snow pants",
        "tank top",
        "cardigan",
        "spring jacket",
        "fall sweater",
      ],
    },
    foods: {
      fruits: [
        "apple",
        "banana",
        "orange",
        "grape",
        "strawberry",
        "blueberry",
        "watermelon",
        "pear",
        "peach",
        "plum",
        "apricot",
        "nectarine",
        "cherry",
        "lemon",
        "lime",
        "grapefruit",
        "pineapple",
        "mango",
        "papaya",
        "kiwi",
        "fig",
        "date",
        "coconut",
        "avocado",
        "blackberry",
        "raspberry",
        "cranberry",
        "currant",
        "gooseberry",
        "pomegranate",
        "guava",
        "lychee",
        "passion fruit",
        "dragonfruit",
        "persimmon",
        "cantaloupe",
        "honeydew",
        "melon",
        "tangerine",
        "clementine",
        "mandarin",
        "fruit",
      ],
      vegetables: [
        "vegetable",
        "potato",
        "carrot",
        "onion",
        "garlic",
        "tomato",
        "lettuce",
        "spinach",
        "broccoli",
        "corn",
        "cucumber",
        "zucchini",
        "squash",
        "pumpkin",
        "eggplant",
        "bell pepper",
        "chili pepper",
        "jalapeno",
        "cabbage",
        "kale",
        "brussels sprout",
        "cauliflower",
        "celery",
        "asparagus",
        "artichoke",
        "leek",
        "radish",
        "turnip",
        "beet",
        "parsnip",
        "rutabaga",
        "sweet potato",
        "yam",
        "okra",
        "green bean",
        "pea",
        "snow pea",
        "sugar snap pea",
        "chard",
        "collard greens",
        "mustard greens",
      ],
      legumes: [
        "chickpea",
        "lentil",
        "black bean",
        "kidney bean",
        "pinto bean",
        "navy bean",
        "pea",
        "snow pea",
        "sugar snap pea",
        "edamame",
        "tofu",
        "tempeh",
        "seitan",
        "bean",
        "legume",
      ],
      meat: [
        "steak",
        "chicken",
        "beef",
        "pork",
        "lamb",
        "veal",
        "venison",
        "bison",
        "duck",
        "turkey",
        "goose",
        "quail",
        "pheasant",
        "rabbit",
        "bacon",
        "ham",
        "sausage",
        "salami",
        "pepperoni",
        "prosciutto",
        "hot dog",
        "meat",
      ],
      seafood: [
        "fish",
        "shrimp",
        "lobster",
        "crab",
        "salmon",
        "tuna",
        "cod",
        "halibut",
        "trout",
        "tilapia",
        "mackerel",
        "sardine",
        "anchovy",
        "mussels",
        "clams",
        "oysters",
        "scallops",
        "seafood",
        "sushi",
      ],
      dairy: [
        "cheese",
        "yogurt",
        "milk",
        "cream",
        "butter",
        "margarine",
        "sour cream",
        "cottage cheese",
        "ricotta",
        "mozzarella",
        "cheddar",
        "brie",
        "camembert",
        "gouda",
        "swiss cheese",
        "parmesan",
        "feta",
        "blue cheese",
        "goat cheese",
        "dairy",
        "ice cream",
      ],
      dairy_alternatives: [
        "almond milk",
        "soy milk",
        "oat milk",
        "coconut milk",
        "rice milk",
        "cashew milk",
        "non-dairy",
        "vegan cheese",
        "plant milk",
      ],
      grains: [
        "rice",
        "bread",
        "wheat",
        "oats",
        "barley",
        "rye",
        "quinoa",
        "couscous",
        "bulgur",
        "farro",
        "millet",
        "sorghum",
        "cornmeal",
        "flour",
        "bread crumbs",
        "crouton",
        "grain",
        "cereal",
        "granola",
        "oatmeal",
      ],
      bread_products: [
        "bread",
        "tortilla",
        "naan",
        "pita",
        "bagel",
        "croissant",
        "baguette",
        "roll",
        "biscuit",
        "muffin",
        "pancake",
        "waffle",
        "toast",
        "flatbread",
      ],
      pasta: [
        "pasta",
        "noodle",
        "spaghetti",
        "macaroni",
        "fettuccine",
        "linguine",
        "lasagna",
        "ravioli",
        "dumpling",
        "ramen",
        "penne",
        "rigatoni",
      ],
      fast_food: [
        "pizza",
        "burger",
        "sandwich",
        "hot dog",
        "french fries",
        "nachos",
        "taco",
        "burrito",
        "fast food",
        "take out",
        "drive through",
        "nuggets",
        "onion rings",
      ],
      desserts: [
        "ice cream",
        "cake",
        "cookie",
        "chocolate",
        "candy",
        "pie",
        "tart",
        "cobbler",
        "pudding",
        "custard",
        "brownie",
        "donut",
        "pastry",
        "danish",
        "eclair",
        "cupcake",
        "cheesecake",
        "tiramisu",
        "gelato",
        "sorbet",
        "popsicle",
        "dessert",
        "sweet",
        "treat",
        "baked good",
      ],
      candy_and_sweets: [
        "chocolate",
        "candy",
        "caramel",
        "fudge",
        "taffy",
        "marshmallow",
        "gummy",
        "jelly bean",
        "lollipop",
        "licorice",
        "toffee",
        "brittle",
        "praline",
        "truffle",
        "sweet",
        "confection",
      ],
      condiments_and_sauces: [
        "ketchup",
        "mustard",
        "mayonnaise",
        "vinegar",
        "oil",
        "olive oil",
        "soy sauce",
        "hot sauce",
        "salsa",
        "guacamole",
        "hummus",
        "pesto",
        "marinara",
        "alfredo",
        "gravy",
        "dressing",
        "ranch",
        "honey",
        "syrup",
        "jam",
        "jelly",
        "preserves",
        "peanut butter",
        "nutella",
        "butter",
        "margarine",
        "sauce",
        "condiment",
      ],
      beverages_non_alcoholic: [
        "water",
        "tea",
        "coffee",
        "juice",
        "soda",
        "pop",
        "cola",
        "lemonade",
        "smoothie",
        "milkshake",
        "drink",
        "beverage",
        "sparkling water",
        "mineral water",
        "energy drink",
        "sports drink",
        "hot chocolate",
        "cocoa",
        "chai",
        "kombucha",
        "cider",
      ],
      alcoholic_beverages: [
        "wine",
        "beer",
        "cocktail",
        "whiskey",
        "vodka",
        "rum",
        "tequila",
        "gin",
        "liqueur",
        "champagne",
        "mead",
        "sake",
        "brandy",
        "bourbon",
        "scotch",
        "ale",
        "lager",
        "alcoholic",
        "liquor",
        "spirit",
      ],
      international_cuisine: [
        "sushi",
        "pasta",
        "pizza",
        "taco",
        "burrito",
        "curry",
        "stir fry",
        "pad thai",
        "samosa",
        "falafel",
        "hummus",
        "pierogies",
        "goulash",
        "paella",
        "ramen",
        "croissant",
        "gelato",
        "pho",
        "kimchi",
        "borscht",
        "tandoori",
        "poutine",
        "schnitzel",
      ],
      meal_types: [
        "breakfast",
        "lunch",
        "dinner",
        "brunch",
        "snack",
        "appetizer",
        "entree",
        "main course",
        "side dish",
        "dessert",
        "meal",
        "feast",
        "banquet",
      ],
      cooking_methods: [
        "baking",
        "grilling",
        "frying",
        "roasting",
        "boiling",
        "steaming",
        "sautéing",
        "poaching",
        "simmering",
        "broiling",
        "smoking",
        "barbecue",
        "braising",
        "stewing",
      ],
    },

    transportation: {
      passenger_cars: [
        "car",
        "sedan",
        "coupe",
        "convertible",
        "hatchback",
        "station wagon",
        "SUV",
        "minivan",
        "van",
        "limousine",
        "taxi",
        "cab",
        "rideshare",
        "uber",
        "lyft",
        "police car",
        "automobile",
        "vehicle",
      ],
      trucks_utility: [
        "truck",
        "pickup truck",
        "semi-truck",
        "tractor-trailer",
        "moving truck",
        "dump truck",
        "garbage truck",
        "fire truck",
        "ambulance",
        "utility vehicle",
        "commercial truck",
        "big rig",
        "lorry",
      ],
      buses: [
        "bus",
        "school bus",
        "double-decker bus",
        "trolley",
        "shuttle bus",
        "minibus",
        "coach",
        "passenger bus",
        "transit bus",
        "tour bus",
      ],
      trains_rail: [
        "train",
        "subway",
        "tram",
        "monorail",
        "light rail",
        "passenger train",
        "freight train",
        "bullet train",
        "high-speed rail",
        "steam locomotive",
        "diesel locomotive",
        "electric locomotive",
        "caboose",
        "rail car",
        "metro",
        "underground",
        "tube",
        "streetcar",
        "cable car",
        "railway",
        "railroad",
      ],
      bicycles_human_powered: [
        "bicycle",
        "tricycle",
        "unicycle",
        "tandem bicycle",
        "mountain bike",
        "road bike",
        "BMX bike",
        "electric bike",
        "bike",
        "cycle",
        "pedal bike",
        "push bike",
        "recumbent bike",
      ],
      motorcycles_powered_bikes: [
        "motorcycle",
        "scooter",
        "moped",
        "dirt bike",
        "motorbike",
        "chopper",
        "cruiser",
        "sport bike",
        "touring motorcycle",
        "electric scooter",
      ],
      personal_mobility: [
        "skateboard",
        "segway",
        "hoverboard",
        "roller skates",
        "inline skates",
        "electric skateboard",
        "wheelchair",
        "mobility scooter",
        "scooter",
        "personal transporter",
      ],
      ships_large_vessels: [
        "boat",
        "ship",
        "ferry",
        "yacht",
        "cruise ship",
        "ocean liner",
        "sailboat",
        "naval vessel",
        "tanker",
        "cargo ship",
        "container ship",
        "freighter",
        "passenger ship",
        "barge",
        "vessel",
        "watercraft",
      ],
      small_watercraft: [
        "canoe",
        "kayak",
        "speedboat",
        "motorboat",
        "rowboat",
        "raft",
        "dinghy",
        "pontoon",
        "jet ski",
        "paddleboard",
        "surfboard",
        "small boat",
        "personal watercraft",
      ],
      aircraft_planes: [
        "airplane",
        "jet",
        "propeller plane",
        "seaplane",
        "passenger plane",
        "private jet",
        "fighter jet",
        "airliner",
        "commercial aircraft",
        "aircraft",
        "plane",
        "biplane",
        "monoplane",
        "jumbo jet",
      ],
      helicopters_vertical_takeoff: [
        "helicopter",
        "chopper",
        "rotor aircraft",
        "medevac helicopter",
        "military helicopter",
        "rescue helicopter",
        "vertical takeoff aircraft",
      ],
      spacecraft_orbital: [
        "rocket",
        "spaceship",
        "space shuttle",
        "space station",
        "rover",
        "lander",
        "probe",
        "satellite",
        "spacecraft",
        "orbital vehicle",
        "reentry vehicle",
        "launch vehicle",
        "booster",
        "capsule",
      ],
      lighter_than_air: [
        "hot air balloon",
        "blimp",
        "zeppelin",
        "dirigible",
        "airship",
        "balloon",
        "gas balloon",
        "helium balloon",
        "weather balloon",
      ],
      unmanned_vehicles: [
        "drone",
        "unmanned aerial vehicle",
        "UAV",
        "remote controlled aircraft",
        "autonomous vehicle",
        "self-driving car",
        "robotic vehicle",
      ],
      snow_ice_transport: [
        "sled",
        "toboggan",
        "snowmobile",
        "dogsled",
        "ice boat",
        "snow cat",
        "snow coach",
        "ice vehicle",
        "ski mobility",
        "snowmachine",
      ],
      animal_powered: [
        "horse",
        "donkey",
        "camel",
        "elephant",
        "rickshaw",
        "carriage",
        "buggy",
        "chariot",
        "wagon",
        "cart",
        "sled",
        "horse-drawn",
        "oxcart",
        "animal transport",
      ],
      water_crossing: [
        "gondola",
        "ferry",
        "water taxi",
        "canal boat",
        "amphibious vehicle",
        "hydrofoil",
        "hovercraft",
        "water bus",
        "water transport",
      ],
      aerial_crossing: [
        "gondola",
        "cable car",
        "ski lift",
        "aerial tram",
        "ropeway",
        "funicular",
        "chair lift",
        "aerial lift",
        "sky tram",
      ],
      stationary_transport: [
        "escalator",
        "elevator",
        "moving walkway",
        "conveyor belt",
        "people mover",
        "moving sidewalk",
        "vertical transport",
        "dumbwaiter",
        "lift",
      ],
      recreational_transport: [
        "zipline",
        "hang glider",
        "paraglider",
        "parachute",
        "parasail",
        "water slide",
        "luge track",
        "bobsled run",
        "leisure transportation",
        "fun vehicle",
      ],
      military_vehicles: [
        "tank",
        "armored vehicle",
        "aircraft carrier",
        "battleship",
        "submarine",
        "destroyer",
        "military transport",
        "armored personnel carrier",
        "humvee",
        "jeep",
      ],
    },

    sports: {
      ball_team_large_field: [
        "soccer",
        "football",
        "american football",
        "rugby",
        "aussie rules football",
        "cricket",
        "baseball",
        "softball",
        "lacrosse",
        "field hockey",
        "team sport",
        "field sport",
      ],
      ball_team_court: [
        "basketball",
        "volleyball",
        "handball",
        "netball",
        "dodgeball",
        "kickball",
        "indoor soccer",
        "futsal",
        "korfball",
        "court sport",
      ],
      racket_paddle_sports: [
        "tennis",
        "badminton",
        "table tennis",
        "ping pong",
        "racquetball",
        "squash",
        "pickleball",
        "paddle tennis",
        "platform tennis",
        "racket sport",
        "paddle sport",
      ],
      throwing_precision: [
        "golf",
        "bowling",
        "archery",
        "shooting",
        "darts",
        "billiards",
        "pool",
        "snooker",
        "target shooting",
        "curling",
        "bocce",
        "horseshoes",
        "frisbee golf",
        "disc golf",
        "precision sport",
      ],
      track_field: [
        "track",
        "field",
        "running",
        "sprinting",
        "marathon",
        "hurdles",
        "relay",
        "high jump",
        "long jump",
        "triple jump",
        "pole vault",
        "javelin",
        "discus",
        "shot put",
        "hammer throw",
        "decathlon",
        "pentathlon",
        "heptathlon",
        "athletics",
      ],
      distance_running: [
        "marathon",
        "cross country",
        "jogging",
        "trail running",
        "ultra-marathon",
        "half-marathon",
        "road running",
        "distance running",
        "endurance running",
      ],
      combat_sports: [
        "boxing",
        "wrestling",
        "martial arts",
        "judo",
        "karate",
        "taekwondo",
        "aikido",
        "jiu-jitsu",
        "muay thai",
        "kickboxing",
        "mma",
        "mixed martial arts",
        "sumo",
        "capoeira",
        "kendo",
        "fencing",
        "fighting sport",
        "combat sport",
      ],
      water_sports: [
        "swimming",
        "diving",
        "water polo",
        "synchronized swimming",
        "surfing",
        "bodyboarding",
        "windsurfing",
        "kitesurfing",
        "wakeboarding",
        "waterskiing",
        "paddleboarding",
        "canoeing",
        "kayaking",
        "rafting",
        "rowing",
        "sailing",
        "aquatic sport",
        "water sport",
      ],
      winter_snow_sports: [
        "skiing",
        "snowboarding",
        "ice hockey",
        "figure skating",
        "speed skating",
        "ice dancing",
        "curling",
        "bobsled",
        "luge",
        "skeleton",
        "biathlon",
        "slalom",
        "downhill skiing",
        "cross-country skiing",
        "ski jumping",
        "snowshoeing",
        "snowmobile racing",
        "winter sport",
        "snow sport",
        "ice sport",
      ],
      cycling_wheeled_sports: [
        "cycling",
        "bmx",
        "mountain biking",
        "road cycling",
        "track cycling",
        "bicycle racing",
        "bike racing",
        "bicycle motocross",
        "unicycling",
        "cycle sport",
        "bike sport",
      ],
      motorsports: [
        "motorcycle racing",
        "auto racing",
        "formula 1",
        "nascar",
        "drag racing",
        "rally racing",
        "off-road racing",
        "motocross",
        "supercross",
        "enduro",
        "speedway",
        "motor sport",
        "automotive sport",
        "racing sport",
      ],
      equestrian_sports: [
        "horse racing",
        "polo",
        "dressage",
        "show jumping",
        "eventing",
        "rodeo",
        "barrel racing",
        "equestrian",
        "horseback riding",
        "horse sport",
      ],
      extreme_adventure_sports: [
        "skateboarding",
        "rock climbing",
        "bouldering",
        "mountain climbing",
        "parkour",
        "bungee jumping",
        "skydiving",
        "paragliding",
        "hang gliding",
        "base jumping",
        "cliff diving",
        "freestyle motocross",
        "extreme sport",
        "adventure sport",
        "action sport",
        "adrenaline sport",
      ],
      gymnastics_acrobatics: [
        "gymnastics",
        "trampolining",
        "acrobatics",
        "cheerleading",
        "tumbling",
        "rhythmic gymnastics",
        "artistic gymnastics",
        "balance beam",
        "uneven bars",
        "parallel bars",
        "pommel horse",
        "vault",
        "floor exercise",
        "aerial sport",
      ],
      strength_power_sports: [
        "weightlifting",
        "bodybuilding",
        "powerlifting",
        "strongman",
        "crossfit",
        "strength training",
        "olympic lifting",
        "power sport",
        "strength sport",
      ],
      mind_sports: [
        "chess",
        "checkers",
        "go",
        "backgammon",
        "poker",
        "bridge",
        "esports",
        "competitive gaming",
        "mahjong",
        "board game competition",
        "card game competition",
        "mental sport",
        "mind sport",
        "strategy game",
      ],
      target_shooting_sports: [
        "hunting",
        "skeet shooting",
        "trap shooting",
        "clay pigeon shooting",
        "rifle shooting",
        "pistol shooting",
        "sport shooting",
        "target shooting",
        "biathlon",
        "shooting sport",
      ],
      recreational_activity_sports: [
        "fishing",
        "hiking",
        "backpacking",
        "orienteering",
        "camping",
        "outdoor recreation",
        "recreational sport",
        "leisure sport",
        "outdoor sport",
      ],
      dance_performance_sports: [
        "ballet",
        "dance",
        "ice dancing",
        "figure skating",
        "rhythmic gymnastics",
        "synchronized swimming",
        "ballroom dancing",
        "dancesport",
        "competitive dance",
        "performance sport",
        "artistic sport",
      ],
      fitness_exercise: [
        "yoga",
        "pilates",
        "tai chi",
        "qigong",
        "aerobics",
        "zumba",
        "fitness",
        "exercise",
        "workout",
        "training",
        "calisthenics",
        "fitness sport",
        "exercise sport",
      ],
      alternative_sports: [
        "ultimate frisbee",
        "paintball",
        "airsoft",
        "laser tag",
        "quidditch",
        "roller derby",
        "sepak takraw",
        "kabaddi",
        "hurling",
        "gaelic football",
        "australian football",
        "jai alai",
        "non-traditional sport",
        "alternative sport",
      ],
    },

    hobbies: {
      reading_writing: [
        "reading",
        "writing",
        "book club",
        "literary criticism",
        "poetry",
        "novels",
        "fiction",
        "non-fiction",
        "biographies",
        "memoirs",
        "journaling",
        "blogging",
        "creative writing",
        "screenwriting",
        "playwriting",
        "copywriting",
        "technical writing",
        "proofreading",
        "editing",
        "publishing",
        "bookbinding",
        "calligraphy",
        "letter writing",
        "storytelling",
        "literature",
        "author",
        "poet",
      ],
      visual_arts: [
        "painting",
        "drawing",
        "oil painting",
        "watercolor painting",
        "acrylic painting",
        "sketching",
        "illustration",
        "graphic design",
        "digital art",
        "animation",
        "cartooning",
        "caricature",
        "portraiture",
        "landscape painting",
        "still life",
        "abstract art",
        "printmaking",
        "linocut",
        "etching",
        "screen printing",
        "pastel",
        "charcoal drawing",
        "ink drawing",
        "art",
        "artist",
        "sculpting",
        "sculpture",
        "adult coloring books",
      ],
      photography_film: [
        "photography",
        "portrait photography",
        "landscape photography",
        "wildlife photography",
        "macro photography",
        "street photography",
        "fashion photography",
        "sports photography",
        "night photography",
        "aerial photography",
        "underwater photography",
        "photojournalism",
        "photo editing",
        "filmmaking",
        "videography",
        "documentary",
        "vlogging",
        "stop motion",
        "time-lapse photography",
        "photographer",
        "cinematographer",
      ],
      crafts_diy: [
        "knitting",
        "crochet",
        "sewing",
        "embroidery",
        "cross-stitch",
        "needlepoint",
        "quilting",
        "appliqué",
        "macramé",
        "weaving",
        "loom weaving",
        "tapestry",
        "rug making",
        "felting",
        "leatherworking",
        "jewelry making",
        "beading",
        "wire wrapping",
        "candle making",
        "soap making",
        "paper crafting",
        "card making",
        "scrapbooking",
        "decoupage",
        "basket weaving",
        "furniture restoration",
        "upholstery",
        "glass blowing",
        "stained glass",
        "mosaic",
        "pottery",
        "ceramics",
        "model building",
        "miniature painting",
        "dollhouse making",
        "terrarium building",
        "crafting",
        "crafts",
        "handmade",
        "diy",
        "do it yourself",
      ],
      gardening_plants: [
        "gardening",
        "flower arranging",
        "ikebana",
        "bonsai",
        "home decorating",
        "upcycling",
        "repurposing",
        "plant growing",
        "horticulture",
        "landscaping",
        "vegetable gardening",
        "herb garden",
        "garden",
        "plants",
        "flowers",
        "botany",
      ],
      woodworking_construction: [
        "woodworking",
        "carpentry",
        "furniture making",
        "wood carving",
        "cabinet making",
        "woodturning",
        "whittling",
        "joinery",
        "construction",
        "home improvement",
        "renovation",
        "building",
        "woodwork",
      ],
      outdoor_activities: [
        "fishing",
        "hunting",
        "camping",
        "hiking",
        "bird watching",
        "wildlife spotting",
        "stargazing",
        "astronomy",
        "rock collecting",
        "fossil hunting",
        "metal detecting",
        "beachcombing",
        "foraging",
        "mushroom hunting",
        "botany",
        "herbalism",
        "backpacking",
        "mountaineering",
        "rock climbing",
        "bouldering",
        "caving",
        "spelunking",
        "outdoors",
        "outdoor",
        "nature",
        "wilderness",
        "exploration",
      ],
      water_activities: [
        "swimming",
        "kayaking",
        "canoeing",
        "rafting",
        "paddleboarding",
        "surfing",
        "windsurfing",
        "kitesurfing",
        "sailing",
        "boating",
        "scuba diving",
        "snorkeling",
        "water sports",
        "water activities",
      ],
      fitness_exercise: [
        "running",
        "biking",
        "yoga",
        "weightlifting",
        "bodybuilding",
        "crossfit",
        "pilates",
        "tai chi",
        "qigong",
        "martial arts",
        "karate",
        "taekwondo",
        "judo",
        "jiu-jitsu",
        "boxing",
        "kickboxing",
        "wrestling",
        "gymnastics",
        "acrobatics",
        "parkour",
        "trampoline",
        "roller skating",
        "ice skating",
        "skateboarding",
        "fitness",
        "exercise",
        "workout",
        "training",
        "conditioning",
        "stretching",
      ],
      winter_sports: [
        "snowboarding",
        "skiing",
        "snowshoeing",
        "ice skating",
        "hockey",
        "ice fishing",
        "winter sports",
        "snow sports",
      ],
      team_sports: [
        "basketball",
        "football",
        "soccer",
        "baseball",
        "softball",
        "cricket",
        "rugby",
        "hockey",
        "lacrosse",
        "ultimate frisbee",
        "handball",
        "water polo",
        "volleyball",
        "team sports",
        "sports team",
      ],
      individual_sports: [
        "tennis",
        "badminton",
        "table tennis",
        "racquetball",
        "squash",
        "pickleball",
        "golfing",
        "disc golf",
        "bowling",
        "fencing",
        "archery",
        "individual sports",
        "solo sports",
        "track and field",
        "swimming",
        "cycling",
        "running",
      ],
      extreme_sports: [
        "skydiving",
        "paragliding",
        "hang gliding",
        "bungee jumping",
        "rock climbing",
        "mountain biking",
        "BMX",
        "motorcycle riding",
        "ATV riding",
        "extreme sports",
        "action sports",
        "adrenaline sports",
      ],
      music_playing: [
        "playing music",
        "piano playing",
        "guitar playing",
        "violin playing",
        "cello playing",
        "flute playing",
        "clarinet playing",
        "saxophone playing",
        "trumpet playing",
        "drums playing",
        "percussion",
        "harmonica playing",
        "ukulele playing",
        "banjo playing",
        "mandolin playing",
        "harp playing",
        "accordion playing",
        "bagpipes playing",
        "instrument",
        "musician",
        "music",
      ],
      music_creation: [
        "beatboxing",
        "DJ-ing",
        "music production",
        "composing",
        "songwriting",
        "music theory",
        "record producing",
        "audio engineering",
        "music composition",
        "sound design",
        "music arrangement",
      ],
      singing_vocal: [
        "singing",
        "choral singing",
        "opera singing",
        "a cappella",
        "barbershop quartet",
        "vocalist",
        "choir",
        "vocal",
        "voice",
      ],
      performing_arts: [
        "dancing",
        "musical theater",
        "acting",
        "theater",
        "improv comedy",
        "stand-up comedy",
        "mime",
        "puppetry",
        "ventriloquism",
        "magic",
        "juggling",
        "circus arts",
        "ballet",
        "tap dancing",
        "ballroom dancing",
        "salsa dancing",
        "swing dancing",
        "tango",
        "hip hop dancing",
        "breakdancing",
        "folk dancing",
        "line dancing",
        "belly dancing",
        "pole dancing",
        "hula",
        "flamenco",
        "performer",
        "performance",
        "stage",
        "entertainment",
      ],
      cooking_food: [
        "cooking",
        "baking",
        "wine tasting",
        "beer tasting",
        "food tasting",
        "cooking clubs",
        "dinner parties",
        "hosting",
        "chef",
        "culinary",
        "recipes",
        "barbeque",
        "grilling",
        "pastry making",
        "bread making",
        "cake decorating",
        "food",
        "cuisine",
        "gourmet",
      ],
      collecting: [
        "collecting",
        "stamp collecting",
        "coin collecting",
        "currency collecting",
        "antique collecting",
        "vintage items",
        "record collecting",
        "comic book collecting",
        "book collecting",
        "art collecting",
        "memorabilia",
        "sports memorabilia",
        "autograph collecting",
        "model cars",
        "model trains",
        "action figures",
        "toy collecting",
        "doll collecting",
        "shell collecting",
        "rock collecting",
        "gemstone collecting",
        "mineral collecting",
        "fossil collecting",
        "insect collecting",
        "butterfly collecting",
        "taxidermy",
        "wine collecting",
        "whiskey collecting",
        "collection",
        "collector",
      ],
      brewing_distilling: [
        "beer brewing",
        "wine making",
        "distilling",
        "homebrewing",
        "craft brewing",
        "fermentation",
        "mead making",
        "cider making",
        "beverage making",
      ],
      games_tabletop: [
        "chess",
        "cards",
        "board games",
        "tabletop games",
        "role-playing games",
        "dungeons and dragons",
        "miniature wargaming",
        "collectible card games",
        "magic the gathering",
        "poker",
        "bridge",
        "mahjong",
        "dominoes",
        "backgammon",
        "checkers",
        "go",
        "boardgame",
        "card game",
        "tabletop",
      ],
      puzzles_brain_games: [
        "puzzles",
        "sudoku",
        "crossword puzzles",
        "word searches",
        "jigsaw puzzles",
        "rubik's cube",
        "mechanical puzzles",
        "escape rooms",
        "brain teasers",
        "riddles",
        "logic puzzles",
        "puzzle",
        "brain games",
      ],
      gaming_digital: [
        "gaming",
        "video games",
        "retro gaming",
        "console gaming",
        "PC gaming",
        "mobile gaming",
        "VR gaming",
        "esports",
        "game development",
        "game design",
        "gamer",
        "digital games",
        "video game",
      ],
      technology_programming: [
        "programming",
        "app development",
        "web development",
        "3D modeling",
        "animation",
        "coding",
        "software development",
        "game development",
        "robotics",
        "electronics",
        "computer",
        "tech",
        "technology",
        "IT",
        "artificial intelligence",
        "machine learning",
      ],
      learning_education: [
        "language learning",
        "history study",
        "archaeology",
        "genealogy",
        "philosophy",
        "theology",
        "science",
        "astronomy",
        "physics",
        "chemistry",
        "biology",
        "botany",
        "zoology",
        "mathematics",
        "engineering",
        "architecture",
        "psychology",
        "sociology",
        "anthropology",
        "geography",
        "politics",
        "economics",
        "law study",
        "medicine study",
        "anatomy",
        "neuroscience",
        "linguistics",
        "etymology",
        "lexicography",
        "education",
        "learning",
        "study",
        "student",
        "academic",
        "intellectual",
      ],
      community_service: [
        "meditation",
        "volunteering",
        "charity work",
        "community service",
        "activism",
        "politics",
        "social clubs",
        "book clubs",
        "film clubs",
        "public speaking",
        "debate",
        "teaching",
        "mentoring",
        "tutoring",
        "volunteering",
        "philanthropy",
        "giving back",
        "social impact",
        "community",
      ],
      travel_exploration: [
        "traveling",
        "cultural exchange",
        "historical reenactment",
        "renaissance faires",
        "museum visiting",
        "gallery visiting",
        "concert going",
        "theater going",
        "opera going",
        "ballet attending",
        "tourism",
        "sightseeing",
        "exploration",
        "world travel",
        "backpacking",
        "road trips",
        "cruise",
        "vacation",
        "journeying",
        "adventure travel",
      ],
      roleplay_fantasy: [
        "reenacting",
        "cosplay",
        "larping",
        "conventions",
        "historical reenactment",
        "fantasy",
        "roleplaying",
        "character",
        "costume",
        "dress up",
        "role play",
      ],
      event_planning: [
        "event planning",
        "party planning",
        "wedding planning",
        "organizing events",
        "coordination",
        "planning",
        "organizing",
        "decorating",
        "celebration",
      ],
    },
    kitchen_items: {
      cookware: [
        "pot",
        "pan",
        "frying pan",
        "sauté pan",
        "saucepan",
        "stock pot",
        "dutch oven",
        "pressure cooker",
        "slow cooker",
        "crock pot",
        "instant pot",
        "wok",
        "griddle",
        "casserole dish",
        "baking dish",
        "roasting pan",
        "sheet pan",
        "cookie sheet",
        "muffin tin",
        "cake pan",
        "pie plate",
        "loaf pan",
        "bundt pan",
        "springform pan",
        "cast iron skillet",
        "non-stick pan",
        "grill pan",
        "double boiler",
        "steamer basket",
        "rice cooker",
        "air fryer",
        "deep fryer",
        "waffle maker",
        "panini press",
      ],

      knives_and_cutting_tools: [
        "knife",
        "butter knife",
        "steak knife",
        "bread knife",
        "carving knife",
        "chef's knife",
        "paring knife",
        "cleaver",
        "santoku knife",
        "boning knife",
        "pizza cutter",
        "vegetable peeler",
        "potato peeler",
        "mandoline",
        "spiralizer",
        "apple corer",
      ],

      measuring_tools: [
        "measuring cup",
        "measuring spoon",
        "kitchen scale",
        "candy thermometer",
        "meat thermometer",
        "kitchen timer",
      ],

      mixing_tools: [
        "whisk",
        "spatula",
        "wooden spoon",
        "mixing bowl",
        "mixer",
        "blender",
        "food processor",
        "pastry brush",
        "basting brush",
        "potato masher",
      ],

      utensils: [
        "spoon",
        "table spoon",
        "tea spoon",
        "serving spoon",
        "slotted spoon",
        "soup spoon",
        "dessert spoon",
        "fork",
        "dinner fork",
        "salad fork",
        "serving fork",
        "carving fork",
        "cocktail fork",
        "tongs",
        "salad tongs",
        "ice tongs",
        "ladle",
        "zester",
        "grater",
        "box grater",
        "cheese grater",
        "microplane",
        "citrus reamer",
        "garlic press",
        "nutcracker",
        "egg slicer",
        "egg separator",
      ],

      drinkware: [
        "cup",
        "mug",
        "glass",
        "teacup",
        "coffee cup",
        "espresso cup",
        "demitasse cup",
        "tumbler",
        "water glass",
        "wine glass",
        "champagne flute",
        "beer glass",
        "shot glass",
        "coffee mug",
        "travel mug",
        "thermos",
      ],

      dishware: [
        "bowl",
        "plate",
        "dinner plate",
        "salad plate",
        "appetizer plate",
        "dessert plate",
        "soup bowl",
        "cereal bowl",
        "serving bowl",
        "butter dish",
        "gravy boat",
        "sauce boat",
        "sugar bowl",
        "creamer",
        "milk pitcher",
        "serving platter",
        "charger plate",
        "cake stand",
      ],

      beverage_appliances: [
        "kettle",
        "electric kettle",
        "tea kettle",
        "coffee maker",
        "espresso machine",
        "french press",
        "pour-over",
        "wine opener",
        "corkscrew",
      ],

      major_appliances: [
        "microwave",
        "oven",
        "toaster",
        "toaster oven",
        "convection oven",
        "stove",
        "gas range",
        "electric range",
        "induction cooktop",
        "dishwasher",
        "refrigerator",
        "freezer",
        "wine refrigerator",
        "ice maker",
      ],

      cleaning_organization: [
        "dish rack",
        "drying rack",
        "trash can",
        "compost bin",
        "recycling bin",
        "garbage disposal",
        "soap dispenser",
        "utensil holder",
        "knife block",
        "spice rack",
        "spice jar",
        "cookbook stand",
        "paper towel holder",
        "pot rack",
        "under-cabinet lighting",
        "range hood",
        "exhaust fan",
        "water filter",
      ],

      food_prep_misc: [
        "cutting board",
        "cheese board",
        "carving board",
        "serving tray",
        "pizza stone",
        "pizza peel",
        "juicer",
        "citrus juicer",
        "melon baller",
        "funnel",
        "sifter",
        "flour sifter",
        "baster",
        "turkey baster",
      ],

      storage_items: [
        "food storage container",
        "tupperware",
        "plastic wrap",
        "aluminum foil",
        "parchment paper",
        "wax paper",
        "freezer bag",
        "sandwich bag",
        "ziplock bag",
      ],

      linens_textiles: [
        "dish towel",
        "kitchen towel",
        "dish cloth",
        "pot holder",
        "oven mitt",
        "apron",
        "napkin",
        "table cloth",
        "place mat",
        "kitchen rug",
      ],

      tabletop_accessories: [
        "salt shaker",
        "pepper shaker",
        "salt and pepper mills",
        "oil dispenser",
        "vinegar dispenser",
        "coaster",
        "trivet",
        "napkin holder",
        "ice bucket",
        "wine bucket",
        "champagne bucket",
      ],
    },
  };

  // Fetch high scores when component mounts
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // Reference the leaderboard document for "category-fluency"
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "category-fluency"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Use the current difficulty as the key
          const scores =
            data.leaderboards && data.leaderboards[difficulty]
              ? data.leaderboards[difficulty]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, difficulty]);

  // Start timer when game starts
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Focus on input field during gameplay
  useEffect(() => {
    if (gameMode === "play" && isPlaying && inputRef.current) {
      inputRef.current.focus();
    }
  }, [gameMode, isPlaying, currentCategory]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Select a random category from the appropriate set
  const getRandomCategory = (excludeCategory = null) => {
    const categories = categorySets[difficulty];
    let availableCategories = categories;

    if (excludeCategory) {
      availableCategories = categories.filter((cat) => cat !== excludeCategory);
    }

    return availableCategories[
      Math.floor(Math.random() * availableCategories.length)
    ];
  };

  // Start a new game
  const startGame = () => {
    console.log("Starting game...");

    // Reset game state
    setScore(0);
    setSubmittedItems([]);
    setCategoryChangeCount(0);
    setTotalItemsGenerated(0);
    setUniqueItemsGenerated(0);
    setCategorySwitchSpeed([]);
    setValidSubmissions([]);
    setInvalidSubmissions([]);
    setBlockedItems([]);
    setClustersFormed(0);

    // Set time based on difficulty
    const gameTime = difficultySettings[difficulty].totalGameTime;
    setTimeLeft(gameTime);

    // Select first category and next category
    const firstCategory = getRandomCategory();
    const nextCat = getRandomCategory(firstCategory);
    setCurrentCategory(firstCategory);
    setNextCategory(nextCat);
    setCurrentCategoryStartTime(Date.now());

    // Set playing state
    setIsPlaying(true);
    gameActive.current = true;
    setGameStartTime(Date.now());
    setGameEndTime(null);

    // Clear input
    setInputValue("");
    setFeedback(null);

    // Focus input field
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const checkIfItemsAreRelated = (item1, item2) => {
    const normalize = (s) => s.toLowerCase().trim();

    const i1 = normalize(item1);
    const i2 = normalize(item2);

    // Basic string similarity checks
    const stemMatch = i1.slice(0, 4) === i2.slice(0, 4);
    const suffixMatch = i1.slice(-3) === i2.slice(-3);
    const includesMatch = i1.includes(i2) || i2.includes(i1);

    // Semantic group match
    if (semanticGroups[currentCategory]) {
      for (const members of Object.values(semanticGroups[currentCategory])) {
        if (members.includes(i1) && members.includes(i2)) {
          return true;
        }
      }
    }

    return stemMatch || suffixMatch || includesMatch;
  };

  // Check if two items are related and form a cluster
  const endGame = () => {
    console.log("Ending game...");

    clearInterval(timerRef.current);
    setIsPlaying(false);
    gameActive.current = false;
    setGameEndTime(Date.now());

    if (timeLeft === 0) {
      setSessionCompleted(true);
    }

    if (categorySwitchSpeed.length > 0) {
      const avg =
        categorySwitchSpeed.reduce((a, b) => a + b, 0) /
        categorySwitchSpeed.length;
      setAverageSwitchSpeed(Math.round(avg));
    }

    setGameMode("results");
  };

  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Handle item submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isPlaying || !inputValue.trim()) return;

    const trimmedInput = inputValue.trim().toLowerCase();

    // Check if item was already submitted
    if (
      submittedItems.some((item) => item.text.toLowerCase() === trimmedInput)
    ) {
      setFeedback({
        type: "error",
        message: "You already submitted this item!",
      });
      setTimeout(() => setFeedback(null), 1500);
      return;
    }

    // Check for blocked items (common words that don't belong to any category)
    const commonBlockedWords = [
      "the",
      "and",
      "of",
      "in",
      "is",
      "a",
      "to",
      "for",
    ];
    if (commonBlockedWords.includes(trimmedInput)) {
      setFeedback({
        type: "error",
        message: "That's not a specific item!",
      });
      setTimeout(() => setFeedback(null), 1500);
      setBlockedItems((prev) => [...prev, trimmedInput]);
      return;
    }

    // Validate submission based on category
    const isValid = validateSubmission(trimmedInput, currentCategory);

    if (isValid) {
      // Add to submitted items
      const newItem = {
        text: trimmedInput,
        category: currentCategory,
        timestamp: Date.now(),
      };

      setSubmittedItems((prev) => [...prev, newItem]);
      setTotalItemsGenerated((prev) => prev + 1);
      setUniqueItemsGenerated((prev) => prev + 1);
      setValidSubmissions((prev) => [...prev, trimmedInput]);

      // Calculate score based on word length and other factors
      const itemScore = calculateItemScore(trimmedInput, currentCategory);
      setScore((prev) => prev + itemScore);

      // Check for clustering (semantic relatedness to previous items)
      checkForClustering(trimmedInput, currentCategory);

      setFeedback({
        type: "success",
        message: `+${itemScore} points!`,
      });
    } else {
      setInvalidSubmissions((prev) => [...prev, trimmedInput]);
      setFeedback({
        type: "error",
        message: "That doesn't seem to fit the category.",
      });
    }

    // Clear input
    setInputValue("");
    setTimeout(() => setFeedback(null), 1500);
  };

  // Validate if submission belongs to current category
  const validateSubmission = (item, category) => {
    const threshold = difficultySettings[difficulty].validationThreshold;

    // If the category exists in our validation data
    if (commonCategoryItems[category]) {
      // For lenient validation, we just check if it's in our list
      if (threshold === "lenient") {
        return commonCategoryItems[category].includes(item.toLowerCase());
      }

      // For strict validation, we do exact matches only
      if (threshold === "strict") {
        return commonCategoryItems[category].includes(item.toLowerCase());
      }

      // For standard validation, we do exact matches
      return commonCategoryItems[category].includes(item.toLowerCase());
    }

    // Fallback: if we don't have validation data, accept it
    return true;
  };

  // Calculate score for an item
  const calculateItemScore = (item, category) => {
    // Base score based on word length
    let score = Math.max(3, Math.min(10, item.length));

    // Bonus for less common words (simplified version)
    if (commonCategoryItems[category]) {
      const isCommon = commonCategoryItems[category].includes(
        item.toLowerCase()
      );
      if (!isCommon) {
        score += 5; // Bonus for less common items
      }
    }

    return score;
  };

  // Check for semantic clustering
  const checkForClustering = (item, category) => {
    // Get all previous items in this category
    const categoryItems = submittedItems
      .filter((i) => i.category === category)
      .map((i) => i.text.toLowerCase());

    // Only check for clustering with recent items (last 5)
    const recentItems = categoryItems.slice(-5);

    if (recentItems.length > 0) {
      // 1. Check for semantic group clusters
      if (semanticGroups[category]) {
        // Check which semantic group the new item belongs to
        for (const [groupName, members] of Object.entries(
          semanticGroups[category]
        )) {
          if (members.includes(item.toLowerCase())) {
            // Check if any recent items also belong to this group
            const sameGroupItems = recentItems.filter(
              (recent) =>
                members.includes(recent.toLowerCase()) &&
                recent.toLowerCase() !== item.toLowerCase()
            );

            if (sameGroupItems.length > 0) {
              console.log(`Cluster found in semantic group: ${groupName}`);
              setClustersFormed((prev) => prev + 1);
              setScore((prev) => prev + 5); // Larger bonus for semantic clustering

              // Provide feedback about the cluster
              setFeedback({
                type: "success",
                message: `Cluster bonus! +5 points for ${groupName} group`,
              });

              return true;
            }
          }
        }
      }

      // 2. If no semantic group match, fall back to string matching
      // a) Check for word stem matches (more flexible than exact prefixes)
      const stem = item.toLowerCase().substring(0, 4);
      const stemMatch = recentItems.some(
        (recent) =>
          recent.toLowerCase().includes(stem) &&
          recent.toLowerCase() !== item.toLowerCase()
      );

      // b) Check for suffix matches
      const suffix = item.toLowerCase().substring(Math.max(0, item.length - 4));
      const suffixMatch = recentItems.some(
        (recent) =>
          recent.toLowerCase().endsWith(suffix) &&
          recent.length > 4 &&
          recent.toLowerCase() !== item.toLowerCase()
      );

      // c) Check compound words relationships
      const compoundMatch = recentItems.some(
        (recent) =>
          (item.toLowerCase().includes(recent.toLowerCase()) ||
            recent.toLowerCase().includes(item.toLowerCase())) &&
          recent.toLowerCase() !== item.toLowerCase() &&
          recent.length > 3 &&
          item.length > 3
      );

      if (stemMatch || suffixMatch || compoundMatch) {
        console.log("Cluster found through string matching");
        setClustersFormed((prev) => prev + 1);
        setScore((prev) => prev + 3); // Smaller bonus for string-based clustering

        // Provide feedback
        setFeedback({
          type: "success",
          message: "Cluster bonus! +3 points for related words",
        });

        return true;
      }
    }

    return false;
  };

  // Handle category switch
  const handleCategorySwitch = () => {
    if (!isPlaying) return;

    // Check if minimum items have been submitted for this category
    const itemsInCurrentCategory = submittedItems.filter(
      (item) => item.category === currentCategory
    ).length;

    const minRequired = difficultySettings[difficulty].minimumItemsBeforeSwitch;

    if (itemsInCurrentCategory < minRequired) {
      setFeedback({
        type: "error",
        message: `Need ${
          minRequired - itemsInCurrentCategory
        } more items before switching!`,
      });
      setTimeout(() => setFeedback(null), 1500);
      return;
    }

    // Calculate time spent on current category
    const timeSpent = (Date.now() - currentCategoryStartTime) / 1000;
    setCategorySwitchSpeed((prev) => [...prev, timeSpent]);

    // Apply time penalty for voluntary switch
    const penalty = difficultySettings[difficulty].switchPenalty;
    setTimeLeft((prev) => Math.max(0, prev - penalty));

    // Visual feedback for category switch
    setSwitchingCategoryNow(true);
    setTimeout(() => setSwitchingCategoryNow(false), 1000);

    // Switch categories
    setCurrentCategory(nextCategory);
    setNextCategory(getRandomCategory(nextCategory));
    setCurrentCategoryStartTime(Date.now());
    setCategoryChangeCount((prev) => prev + 1);

    // Bonus points for successful category switch
    setScore((prev) => prev + 15);

    setFeedback({
      type: "info",
      message: "Category switched! +15 points",
    });
    setTimeout(() => setFeedback(null), 1500);

    // Focus on input
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  // Handle the tutorial steps
  const advanceTutorial = () => {
    if (tutorialStep < 4) {
      setTutorialStep((prev) => prev + 1);
    } else {
      // End tutorial and start the game
      setGameMode("play");
      startGame();
    }
  };

  // Save score to Firebase
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // Determine the username with fallbacks
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // ⬅️ Unified gameData object to be used in BOTH leaderboard and gameHistory
      const gameData = {
        gameType: "category-fluency", // Used for stats view
        gameId: "category-fluency", // Used for leaderboard and historical integrity
        mode: "single", // Mode tracking
        userId: user.email,
        userName,
        score,
        difficulty,
        totalItemsGenerated,
        uniqueItemsGenerated,
        validCount: validSubmissions.length,
        invalidCount: invalidSubmissions.length,
        categoryChangeCount,
        averageSwitchSpeed,
        clustersFormed,
        gameLength: gameEndTime - gameStartTime,
        timestamp: new Date(),
      };

      // References to the leaderboard and user doc
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "category-fluency"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Composite comparator logic to rank the top 5
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.totalItemsGenerated !== b.totalItemsGenerated)
          return b.totalItemsGenerated - a.totalItemsGenerated;
        if (a.categoryChangeCount !== b.categoryChangeCount)
          return b.categoryChangeCount - a.categoryChangeCount;
        if (a.averageSwitchSpeed !== b.averageSwitchSpeed)
          return a.averageSwitchSpeed - b.averageSwitchSpeed;
        return a.timestamp - b.timestamp;
      };

      // 🔁 Transaction for leaderboard + cumulative score
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const levelKey = difficulty;
        let currentLeaderboard = leaderboardData.leaderboards[levelKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[levelKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 📜 Save exact same gameData to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );
      await setDoc(gameHistoryRef, gameData);

      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-emerald-300">
              Category Fluency
            </h1>
            <div className="p-2 bg-emerald-500/10 rounded-full">
              <Tag className="text-emerald-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                      <Tag className="text-emerald-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Category Fluency Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h3 className="text-emerald-300 font-medium mb-2">
                      Game Overview
                    </h3>
                    <p className="text-slate-300">
                      Category Fluency challenges your verbal fluency and
                      cognitive flexibility – the ability to rapidly generate
                      items within categories and shift between different
                      conceptual domains. This skill is essential for
                      problem-solving and finding alternatives to habitual
                      responses.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-emerald-300 font-medium mb-2">
                      How to Play
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-slate-300">
                      <li>
                        You'll be shown a category (e.g., "Animals", "Foods")
                      </li>
                      <li>
                        Type and submit as many items in that category as you
                        can
                      </li>
                      <li>
                        After submitting a minimum number of items, you can
                        switch to the next category
                      </li>
                      <li>
                        Each correct item earns points based on uniqueness and
                        complexity
                      </li>
                      <li>
                        Switching categories gives bonus points but costs time
                      </li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-emerald-300 font-medium mb-2">
                      Difficulty Levels
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        <span className="font-medium">Easy:</span> Fewer items
                        required before switching, smaller time penalty, common
                        categories
                      </li>
                      <li>
                        <span className="font-medium">Standard:</span> Balanced
                        requirements, moderate time penalty, mix of common and
                        specialized categories
                      </li>
                      <li>
                        <span className="font-medium">Advanced:</span> More
                        items required before switching, larger time penalty,
                        includes specialized categories
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-emerald-300 font-medium mb-2">
                      Recovery Benefits
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        Strengthens impulse control by requiring thoughtful item
                        generation
                      </li>
                      <li>
                        Improves verbal fluency often impaired by substance use
                      </li>
                      <li>
                        Enhances cognitive flexibility by practicing category
                        switching
                      </li>
                      <li>
                        Develops the ability to generate alternative responses
                        and solutions
                      </li>
                    </ul>
                  </div>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-3 rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Tag size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Category Fluency
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Generate items rapidly within categories and switch between
                  different conceptual domains. Improve your verbal fluency and
                  mental flexibility.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-emerald-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-emerald-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Verbal Fluency
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Enhances ability to rapidly generate words and ideas
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-emerald-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-emerald-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">3-5 minutes</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Quick sessions to build rapid thinking skills
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-emerald-500/10 rounded-full mb-2">
                    <Shuffle size={24} className="text-emerald-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Impulse Control
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Practice thoughtful responses instead of automatic reactions
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {["easy", "standard", "advanced"].map((level) => (
                      <button
                        key={level}
                        onClick={() => setDifficulty(level)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors capitalize ${
                          difficulty === level
                            ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => {
                      setGameMode("play");
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Items
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Categories
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((score, index) => {
                          // Format the name to show first name and first letter of last name
                          let formattedName = score.userName;

                          if (score.userName && score.userName.includes(" ")) {
                            const nameParts = score.userName.split(" ");
                            const firstName = nameParts[0];
                            const lastInitial = nameParts[1]
                              ? nameParts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }

                          return (
                            <tr
                              key={index}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300 capitalize">
                                {score.difficulty}
                              </td>
                              <td className="py-2 px-4 text-center text-emerald-300">
                                {score.totalItemsGenerated || 0}
                              </td>
                              <td className="py-2 px-4 text-center text-emerald-300">
                                {score.categoryChangeCount || 0}
                              </td>
                              <td className="py-2 px-4 text-right text-emerald-300 font-medium">
                                {score.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Category Fluency
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Category Fluency challenges your verbal fluency and
                        cognitive flexibility – the ability to think of many
                        different items within a category and to switch between
                        different categories.
                      </p>
                      <p className="text-slate-300">
                        These skills are important during recovery because they
                        help you generate alternative responses to triggers and
                        adapt to new situations.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Here's how the game works:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-slate-300">
                        <li>
                          You'll be shown a category (like "Animals" or "Foods")
                        </li>
                        <li>Type as many items in that category as you can</li>
                        <li>Each valid item earns you points</li>
                        <li>You can't repeat items you've already entered</li>
                        <li>
                          The more unique or complex your answers, the more
                          points you earn
                        </li>
                      </ul>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        After you've submitted enough items in a category, you
                        can switch to a new category.
                      </p>
                      <p className="text-slate-300">
                        Switching categories gives you bonus points but costs
                        you some time. This represents the mental effort of
                        shifting your thinking to a new domain.
                      </p>
                      <p className="text-slate-300">
                        The game measures not just how many items you can
                        generate, but how well you can switch between different
                        types of thinking.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 3 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        The game tracks "clustering" – generating semantically
                        related items together.
                      </p>

                      <div className="bg-slate-700/30 p-3 rounded-lg">
                        <h5 className="text-white font-medium mb-2">
                          Cluster Example:
                        </h5>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <div className="bg-slate-600 text-white text-sm px-3 py-1 rounded-full border-2 border-emerald-400">
                            Shoes
                          </div>
                          <div className="bg-slate-600 text-white text-sm px-3 py-1 rounded-full border-2 border-emerald-400">
                            Boots
                          </div>
                          <div className="bg-slate-600 text-white text-sm px-3 py-1 rounded-full border-2 border-emerald-400">
                            Sandals
                          </div>
                        </div>
                        <p className="text-sm text-emerald-300">
                          These items form a cluster because they're all types
                          of footwear
                        </p>
                      </div>

                      <p className="text-slate-300">
                        Clusters earn you bonus points and demonstrate your
                        brain's ability to organize information by semantic
                        relationships.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 4 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        You're ready to play! Here's what to remember:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-slate-300">
                        <li>
                          Generate as many items as you can in each category
                        </li>
                        <li>Switch categories after submitting enough items</li>
                        <li>
                          Balance your time between generating items and
                          switching categories
                        </li>
                        <li>Try to be both quick and accurate</li>
                      </ul>
                      <p className="text-slate-300">
                        This exercise strengthens both verbal fluency and
                        cognitive flexibility, which supports recovery by
                        expanding your response options beyond habitual
                        patterns.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tutorial visualization */}
              <div className="flex flex-col md:flex-row gap-6 items-center">
                {tutorialStep >= 1 && (
                  <div className="flex-1 flex flex-col items-center">
                    <h3 className="text-white font-medium mb-4">Example</h3>
                    <div className="w-full max-w-xs bg-slate-800 rounded-lg p-4">
                      <div className="mb-4 text-center">
                        <span className="text-lg font-medium text-emerald-300">
                          Category: Animals
                        </span>
                      </div>

                      <div className="bg-slate-700/60 rounded-lg p-3 mb-4">
                        <ul className="text-white space-y-2">
                          <li className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-green-400" />
                            <span>Dog (+5 points)</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-green-400" />
                            <span>Cat (+5 points)</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-green-400" />
                            <span>Elephant (+8 points)</span>
                          </li>
                        </ul>
                      </div>

                      {tutorialStep >= 2 && (
                        <div className="text-center">
                          <button className="py-2 px-4 bg-emerald-500/20 text-emerald-300 rounded-lg border border-emerald-500/30">
                            Switch to: Foods
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex-1 space-y-4">
                  {tutorialStep >= 2 && tutorialStep < 4 && (
                    <div className="bg-slate-700/30 p-4 rounded-lg">
                      <h3 className="text-white font-medium mb-2">
                        Category Switching
                      </h3>
                      <p className="text-slate-300">
                        {tutorialStep === 2
                          ? "After entering enough items, you can switch categories for bonus points but with a time penalty."
                          : "Clustering related items (like 'cat, lion, tiger') shows semantic organization and earns additional points."}
                      </p>
                      <p className="text-emerald-300 mt-2">
                        {tutorialStep === 2
                          ? "Switch strategy: Change categories when you start running out of ideas!"
                          : "Clustering strategy: Try to think of related groups within categories!"}
                      </p>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  <div className="flex justify-between mt-8">
                    {tutorialStep > 0 ? (
                      <button
                        onClick={() => setTutorialStep((prev) => prev - 1)}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Previous
                      </button>
                    ) : (
                      <button
                        onClick={() => setGameMode("intro")}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Back
                      </button>
                    )}

                    <button
                      onClick={advanceTutorial}
                      className="py-2 px-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg transition-colors"
                    >
                      {tutorialStep < 4 ? "Next" : "Start Game"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Game Play Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-emerald-500/20 to-teal-600/20 px-4 py-2 rounded-lg">
                  <span className="text-emerald-300 font-medium capitalize">
                    Level: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-emerald-500/20 to-teal-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center">
                {/* Category Display */}
                <div className="mb-6 w-full max-w-xl">
                  <div className="text-center mb-4">
                    <motion.div
                      key={currentCategory}
                      initial={
                        switchingCategoryNow
                          ? { opacity: 0, y: -20 }
                          : { opacity: 1 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-2xl font-bold text-emerald-300"
                    >
                      Current Category:{" "}
                      {currentCategory.charAt(0).toUpperCase() +
                        currentCategory.slice(1)}
                    </motion.div>
                    <div className="text-sm text-slate-400 mt-1">
                      Next Category:{" "}
                      {nextCategory.charAt(0).toUpperCase() +
                        nextCategory.slice(1)}
                    </div>
                  </div>
                </div>

                {/* Input area */}
                <div className="mb-8 w-full max-w-lg">
                  <form onSubmit={handleSubmit} className="flex gap-4">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={handleInputChange}
                      placeholder={`Type a ${currentCategory} item...`}
                      className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                      disabled={!isPlaying}
                    />
                    <button
                      type="submit"
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                      disabled={!isPlaying || !inputValue.trim()}
                    >
                      Submit
                    </button>
                  </form>
                </div>

                {/* Category Switch Button */}
                <div className="mb-8">
                  <button
                    onClick={handleCategorySwitch}
                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                    disabled={!isPlaying}
                  >
                    <Shuffle size={16} />
                    Switch Category
                  </button>
                  <div className="text-xs text-slate-400 mt-2 text-center">
                    {difficultySettings[difficulty].switchPenalty} second
                    penalty
                  </div>
                </div>

                {/* Feedback message */}
                <AnimatePresence>
                  {feedback && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg ${
                        feedback.type === "success"
                          ? "bg-green-500/20 text-green-300 border border-green-500/30"
                          : feedback.type === "error"
                          ? "bg-red-500/20 text-red-300 border border-red-500/30"
                          : "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                      }`}
                    >
                      {feedback.message}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Items List */}
                <div className="w-full max-w-2xl mx-auto bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-white font-medium">Submitted Items</h3>
                    <span className="text-slate-400 text-sm">
                      {submittedItems.length} total
                    </span>
                  </div>

                  {submittedItems.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      Items you submit will appear here
                    </div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto pr-2">
                      {/* Group items by category */}
                      {Array.from(
                        new Set(submittedItems.map((item) => item.category))
                      ).map((category) => (
                        <div key={category} className="mb-4">
                          <div className="text-emerald-300 text-sm font-medium mb-2 capitalize">
                            {category}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {submittedItems
                              .filter((item) => item.category === category)
                              .map((item, index, array) => {
                                // Determine if this item is part of a cluster
                                const isPartOfCluster = array.some(
                                  (otherItem, otherIndex) => {
                                    if (index === otherIndex) return false;
                                    // Simplified check - you'd use more sophisticated logic in production
                                    return checkIfItemsAreRelated(
                                      item.text,
                                      otherItem.text
                                    );
                                  }
                                );

                                return (
                                  <div
                                    key={index}
                                    className={`bg-slate-700/60 text-white text-sm px-3 py-1 rounded-full 
          ${isPartOfCluster ? "border-2 border-emerald-400" : ""}`}
                                  >
                                    {item.text}
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Game stats */}
              <div className="mt-8 max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">
                    Items Generated
                  </div>
                  <div className="text-white font-medium">
                    {totalItemsGenerated}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Categories</div>
                  <div className="text-white font-medium">
                    {categoryChangeCount + 1}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Valid Items</div>
                  <div className="text-green-400 font-medium">
                    {validSubmissions.length}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">
                    Invalid Items
                  </div>
                  <div className="text-red-400 font-medium">
                    {invalidSubmissions.length}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to end the game early?"
                      )
                    ) {
                      endGame();
                    }
                  }}
                  className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                >
                  End Game
                </button>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You've completed the Category Fluency challenge on{" "}
                  {difficulty} difficulty.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-emerald-400" />
                    Your Results
                  </h3>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-emerald-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium capitalize">
                        {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Total Items Generated
                      </span>
                      <span className="text-emerald-300 font-medium">
                        {totalItemsGenerated}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Categories Explored
                      </span>
                      <span className="text-emerald-300">
                        {categoryChangeCount + 1}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Valid Submissions</span>
                      <span className="text-green-400">
                        {validSubmissions.length}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Invalid Submissions
                      </span>
                      <span className="text-red-400">
                        {invalidSubmissions.length}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Switch Speed</span>
                      <span className="text-white">
                        {averageSwitchSpeed > 0
                          ? `${averageSwitchSpeed} sec`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-emerald-400" />
                    Performance Analysis
                  </h3>

                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">
                          Item Generation Rate
                        </span>
                        <span className="text-white text-sm">
                          {totalItemsGenerated > 0 &&
                          gameEndTime &&
                          gameStartTime
                            ? `${(
                                totalItemsGenerated /
                                ((gameEndTime - gameStartTime) / 1000 / 60)
                              ).toFixed(1)} items/min`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-teal-600"
                          style={{
                            width: `${Math.min(
                              (totalItemsGenerated / 30) * 100,
                              100
                            )}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Verbal Fluency Rating
                      </h4>
                      <div className="bg-slate-700/30 p-3 rounded-lg text-sm">
                        {totalItemsGenerated >= 25 &&
                          validSubmissions.length >= 20 && (
                            <p className="text-green-400">
                              Excellent: You generate items rapidly and
                              accurately, showing strong verbal fluency!
                            </p>
                          )}
                        {totalItemsGenerated >= 15 &&
                          totalItemsGenerated < 25 &&
                          validSubmissions.length >= 12 && (
                            <p className="text-green-300">
                              Good: You show above-average ability to generate
                              relevant items.
                            </p>
                          )}
                        {((totalItemsGenerated >= 10 &&
                          totalItemsGenerated < 15) ||
                          (validSubmissions.length >= 8 &&
                            validSubmissions.length < 12)) && (
                          <p className="text-yellow-300">
                            Developing: You're building verbal fluency with room
                            to grow.
                          </p>
                        )}
                        {(totalItemsGenerated < 10 ||
                          validSubmissions.length < 8) && (
                          <p className="text-orange-300">
                            Emerging: Focus on quickly retrieving relevant items
                            from memory.
                          </p>
                        )}

                        <p className="mt-2 text-slate-300">
                          Valid rate:{" "}
                          {validSubmissions.length > 0
                            ? `${Math.round(
                                (validSubmissions.length /
                                  (validSubmissions.length +
                                    invalidSubmissions.length)) *
                                  100
                              )}%`
                            : "N/A"}{" "}
                          | Clusters formed: {clustersFormed}
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-emerald-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-emerald-400" />
                          </div>
                          <span className="text-slate-300">
                            Strengthens verbal fluency for better communication
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-emerald-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-emerald-400" />
                          </div>
                          <span className="text-slate-300">
                            Improves cognitive flexibility for adapting to new
                            situations
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-emerald-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-emerald-400" />
                          </div>
                          <span className="text-slate-300">
                            Enhances impulse control by practicing deliberate
                            responses
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {sessionCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS for special shapes and fills */}
      <style>
        {`
          /* Animation for category switching */
          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 1; }
            100% { opacity: 0.6; }
          }
          
          .pulse-animation {
            animation: pulse 2s infinite;
          }
        `}
      </style>
    </div>
  );
};

export default CategoryFluency;
